import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import {
  timeEntriesAPI,
  clientsAPI,
  projectsAPI,
  usersAPI,
  organizationsAPI,
  sredReportsAPI,
  Client,
  Project,
  User,
  Organization,
  SavedSredReport,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import XeroIntegration from './XeroIntegration';
import {
  generateSredPdf,
  generateSredSimplePdf,
} from '../../utils/sredPdfExport';
import {
  exportSummaryToExcel,
  exportDetailedToExcel,
} from '../../utils/reportExcelExport';

// Calendar utilities
const getDaysInMonth = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

const getFirstDayOfMonth = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
};

const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const isDateInRange = (
  date: Date,
  startDate: Date | null,
  endDate: Date | null,
): boolean => {
  if (!startDate || !endDate) return false;
  return date >= startDate && date <= endDate;
};

const isDateRangeStart = (date: Date, startDate: Date | null): boolean => {
  return startDate ? isSameDay(date, startDate) : false;
};

const isDateRangeEnd = (date: Date, endDate: Date | null): boolean => {
  return endDate ? isSameDay(date, endDate) : false;
};

const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseStringToDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Date Range Calendar Component
interface DateRangeCalendarProps {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
}

interface CollapsibleDateRangePickerProps {
  filters: ReportFilters;
  selectedPreset: string | null;
  onFiltersChange: React.Dispatch<React.SetStateAction<ReportFilters>>;
  onPresetChange: React.Dispatch<React.SetStateAction<string | null>>;
  onDatePreset: (preset: string) => void;
}

const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({
  startDate,
  endDate,
  onChange,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const startDateObj = startDate ? parseStringToDate(startDate) : null;
  const endDateObj = endDate ? parseStringToDate(endDate) : null;

  const monthNames = [
    t('common.months.january'),
    t('common.months.february'),
    t('common.months.march'),
    t('common.months.april'),
    t('common.months.may'),
    t('common.months.june'),
    t('common.months.july'),
    t('common.months.august'),
    t('common.months.september'),
    t('common.months.october'),
    t('common.months.november'),
    t('common.months.december'),
  ];

  const dayNames = [
    t('common.days.sun'),
    t('common.days.mon'),
    t('common.days.tue'),
    t('common.days.wed'),
    t('common.days.thu'),
    t('common.days.fri'),
    t('common.days.sat'),
  ];

  const handleDateClick = (day: number, month: Date) => {
    const clickedDate = new Date(month.getFullYear(), month.getMonth(), day);
    const clickedDateString = formatDateToString(clickedDate);

    if (selectingStart || !startDateObj) {
      // Selecting start date - set both start and end to the same date initially
      onChange(clickedDateString, '');
      setSelectingStart(false);
    } else if (!selectingStart && startDateObj) {
      // Selecting end date
      if (clickedDate >= startDateObj) {
        // Normal case: end date is after start date
        onChange(startDate, clickedDateString);
      } else {
        // If clicked date is before start date, swap them
        onChange(clickedDateString, startDate);
      }
      setSelectingStart(true);
    } else {
      // Reset and start new selection
      onChange(clickedDateString, '');
      setSelectingStart(false);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const renderCalendarDays = (month: Date) => {
    const daysInMonth = getDaysInMonth(month);
    const firstDay = getFirstDayOfMonth(month);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-7 w-full"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const isStart = isDateRangeStart(date, startDateObj);
      const isEnd = isDateRangeEnd(date, endDateObj);
      const isInRange = isDateInRange(date, startDateObj, endDateObj);
      const isToday = isSameDay(date, new Date());

      // Preview range logic for hover effect
      let previewStart: Date | null = null;
      let previewEnd: Date | null = null;

      if (!selectingStart && startDateObj && hoverDate && !endDateObj) {
        // User has selected start date and is hovering to select end date
        previewStart = startDateObj;
        previewEnd = hoverDate;
        // Ensure start is always before end
        if (hoverDate < startDateObj) {
          previewStart = hoverDate;
          previewEnd = startDateObj;
        }
      }

      const isPreviewInRange =
        previewStart &&
        previewEnd &&
        date >= previewStart &&
        date <= previewEnd &&
        !isInRange &&
        !isStart &&
        !isEnd;
      const isPreviewStart =
        previewStart && isSameDay(date, previewStart) && !isStart;
      const isPreviewEnd = previewEnd && isSameDay(date, previewEnd) && !isEnd;

      let cellClass =
        'h-7 w-full flex items-center justify-center text-sm cursor-pointer transition-colors ';

      if (isStart || isEnd) {
        cellClass += 'bg-violet-600 text-white font-medium ';
        if (isStart && !isEnd) cellClass += 'rounded-l-lg ';
        if (isEnd && !isStart) cellClass += 'rounded-r-lg ';
        if (isStart && isEnd) cellClass += 'rounded-lg ';
      } else if (isInRange) {
        cellClass += isDark
          ? 'bg-violet-500/20 text-violet-300 '
          : 'bg-violet-100 text-violet-800 ';
      } else if (isPreviewStart || isPreviewEnd) {
        cellClass += 'bg-violet-400 text-white font-medium ';
        if (isPreviewStart && !isPreviewEnd) cellClass += 'rounded-l-lg ';
        if (isPreviewEnd && !isPreviewStart) cellClass += 'rounded-r-lg ';
        if (isPreviewStart && isPreviewEnd) cellClass += 'rounded-lg ';
      } else if (isPreviewInRange) {
        cellClass += isDark
          ? 'bg-violet-500/10 text-violet-300 '
          : 'bg-violet-50 text-violet-700 ';
      } else if (isToday) {
        cellClass += isDark
          ? 'bg-slate-700 text-white font-medium rounded-lg '
          : 'bg-slate-100 text-slate-900 font-medium rounded-lg ';
      } else {
        cellClass += isDark
          ? 'text-slate-300 hover:bg-slate-700 rounded-lg '
          : 'text-slate-700 hover:bg-slate-100 rounded-lg ';
      }

      days.push(
        <div
          key={day}
          className={cellClass}
          onClick={() => handleDateClick(day, month)}
          onMouseEnter={() => setHoverDate(date)}
          onMouseLeave={() => setHoverDate(null)}
        >
          {day}
        </div>,
      );
    }

    return days;
  };

  const renderMonth = (
    month: Date,
    showLeftArrow: boolean,
    showRightArrow: boolean,
  ) => (
    <div className="flex-1 min-w-[200px]">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        {showLeftArrow ? (
          <button
            type="button"
            onClick={() => navigateMonth('prev')}
            className={`p-1 rounded-md ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
          >
            <svg
              className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        ) : (
          <div className="w-6 h-6"></div>
        )}

        <h3
          className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {monthNames[month.getMonth()]} {month.getFullYear()}
        </h3>

        {showRightArrow ? (
          <button
            type="button"
            onClick={() => navigateMonth('next')}
            className={`p-1 rounded-md ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
          >
            <svg
              className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        ) : (
          <div className="w-6 h-6"></div>
        )}
      </div>

      {/* Day Names Header */}
      <div className="grid grid-cols-7 mb-1">
        {dayNames.map((name) => (
          <div
            key={name}
            className={`h-6 flex items-center justify-center text-xs font-medium uppercase ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
          >
            {name.substring(0, 2)}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">{renderCalendarDays(month)}</div>
    </div>
  );

  // Calculate next month
  const nextMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    1,
  );

  return (
    <div className={`p-3 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
      {/* Two Month Layout - stacks on small screens */}
      <div className="flex flex-col sm:flex-row gap-4">
        {renderMonth(currentMonth, true, false)}
        <div
          className={`hidden sm:block w-px ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
        ></div>
        <div
          className={`sm:hidden h-px ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
        ></div>
        {renderMonth(nextMonth, false, true)}
      </div>

      {/* Selection Status */}
      <div
        className={`mt-3 text-xs text-center ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
      >
        {!selectingStart && startDateObj && !endDateObj
          ? t('reports.selectEndDate')
          : startDateObj && endDateObj
            ? t('reports.selectNewRange')
            : t('reports.selectStartDate')}
      </div>
    </div>
  );
};

// Collapsible Date Range Picker Component
const CollapsibleDateRangePicker: React.FC<CollapsibleDateRangePickerProps> = ({
  filters,
  selectedPreset,
  onFiltersChange,
  onPresetChange,
  onDatePreset,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  const formatDateRange = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return t('reports.selectDateRange');
    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();
    return start === end ? start : `${start} - ${end}`;
  };

  const getPresetLabel = (preset: string | null) => {
    if (!preset) return null;
    const presetLabels: Record<string, string> = {
      today: t('reports.presets.today'),
      yesterday: t('reports.presets.yesterday'),
      this_week: t('reports.presets.thisWeek'),
      last_week: t('reports.presets.lastWeek'),
      this_month: t('reports.presets.thisMonth'),
      last_month: t('reports.presets.lastMonth'),
      this_year: t('reports.presets.thisYear'),
      last_year: t('reports.presets.lastYear'),
      fiscal_year_minus_2: t('reports.presets.fiscalYearMinus2'),
      fiscal_year_minus_3: t('reports.presets.fiscalYearMinus3'),
    };
    return presetLabels[preset] || null;
  };

  return (
    <div>
      <label className={labelClass}>{t('reports.dateRange')}</label>

      {/* Collapsed State - Shows selected range */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-2.5 rounded-xl text-left transition-colors ${isDark ? 'bg-slate-900/50 border border-slate-700 text-white hover:border-slate-600' : 'bg-white border border-slate-200 text-slate-900 hover:border-slate-300'}`}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm">
            {selectedPreset ? (
              <span className="flex items-center">
                <span className="inline-block w-2 h-2 bg-violet-500 rounded-full mr-2"></span>
                <span className="font-medium">
                  {getPresetLabel(selectedPreset)}
                </span>
                <span
                  className={`ml-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  ({formatDateRange(filters.start_date, filters.end_date)})
                </span>
              </span>
            ) : (
              formatDateRange(filters.start_date, filters.end_date)
            )}
          </span>
          <svg
            className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Expanded State - Shows date picker */}
      {isOpen && (
        <div
          className={`mt-2 rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-slate-200'}`}
        >
          <div className="flex flex-col sm:flex-row">
            {/* Quick Presets Panel */}
            <div
              className={`sm:w-40 border-b sm:border-b-0 sm:border-r ${isDark ? 'border-slate-700 bg-slate-900/50' : 'border-slate-200 bg-slate-50'}`}
            >
              <div
                className={`p-3 border-b hidden sm:block ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
              >
                <h4
                  className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                >
                  {t('reports.quickSelect')}
                </h4>
              </div>
              <div className="p-2 flex flex-row flex-wrap sm:flex-col gap-1 sm:space-y-1 sm:gap-0">
                {[
                  { label: t('reports.presets.today'), value: 'today' },
                  { label: t('reports.presets.yesterday'), value: 'yesterday' },
                  { label: t('reports.presets.thisWeek'), value: 'this_week' },
                  { label: t('reports.presets.lastWeek'), value: 'last_week' },
                  {
                    label: t('reports.presets.thisMonth'),
                    value: 'this_month',
                  },
                  {
                    label: t('reports.presets.lastMonth'),
                    value: 'last_month',
                  },
                  { label: t('reports.presets.thisYear'), value: 'this_year' },
                  { label: t('reports.presets.lastYear'), value: 'last_year' },
                  {
                    label: t('reports.presets.fiscalYearMinus2'),
                    value: 'fiscal_year_minus_2',
                  },
                  {
                    label: t('reports.presets.fiscalYearMinus3'),
                    value: 'fiscal_year_minus_3',
                  },
                ].map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => {
                      onDatePreset(preset.value);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs rounded-lg focus:outline-none transition-colors ${
                      selectedPreset === preset.value
                        ? isDark
                          ? 'bg-violet-500/20 text-violet-300 font-medium'
                          : 'bg-violet-100 text-violet-900 font-medium'
                        : isDark
                          ? 'text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                          : 'text-slate-600 hover:bg-violet-50 hover:text-violet-700'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range Calendar */}
            <div className="flex-1 overflow-x-auto">
              <DateRangeCalendar
                startDate={filters.start_date}
                endDate={filters.end_date}
                onChange={(start, end) => {
                  onFiltersChange((prev) => ({
                    ...prev,
                    start_date: start,
                    end_date: end,
                  }));
                  onPresetChange(null); // Clear preset selection when manually changing dates
                  if (start && end) {
                    setIsOpen(false); // Auto-close when both dates are selected
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Date range utility functions
const getDateRange = (
  preset: string,
  fiscalYearStartMonth: number = 1,
): { start_date: string; end_date: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Convert from 1-indexed (January=1) to 0-indexed (January=0) for JavaScript Date
  const fiscalStartMonthIndex = fiscalYearStartMonth - 1;

  switch (preset) {
    case 'today':
      return {
        start_date: today.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0],
      };

    case 'yesterday':
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        start_date: yesterday.toISOString().split('T')[0],
        end_date: yesterday.toISOString().split('T')[0],
      };

    case 'this_week':
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - today.getDay()); // Sunday
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekStart.getDate() + 6); // Saturday
      return {
        start_date: thisWeekStart.toISOString().split('T')[0],
        end_date: thisWeekEnd.toISOString().split('T')[0],
      };

    case 'last_week':
      const lastWeekStart = new Date(today);
      lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
      return {
        start_date: lastWeekStart.toISOString().split('T')[0],
        end_date: lastWeekEnd.toISOString().split('T')[0],
      };

    case 'this_month':
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        start_date: thisMonthStart.toISOString().split('T')[0],
        end_date: thisMonthEnd.toISOString().split('T')[0],
      };

    case 'last_month':
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        start_date: lastMonthStart.toISOString().split('T')[0],
        end_date: lastMonthEnd.toISOString().split('T')[0],
      };

    case 'this_year':
      // Fiscal year based on organization setting
      // If current month >= fiscal start month, we're in current year's fiscal year
      // Otherwise, we're in last year's fiscal year
      const fiscalYearStart =
        now.getMonth() >= fiscalStartMonthIndex
          ? new Date(now.getFullYear(), fiscalStartMonthIndex, 1)
          : new Date(now.getFullYear() - 1, fiscalStartMonthIndex, 1);
      // Fiscal year ends the day before the next fiscal year starts
      const fiscalYearEnd = new Date(
        fiscalYearStart.getFullYear() + 1,
        fiscalStartMonthIndex,
        0,
      );
      return {
        start_date: fiscalYearStart.toISOString().split('T')[0],
        end_date: fiscalYearEnd.toISOString().split('T')[0],
      };

    case 'last_year':
      const lastFiscalStart =
        now.getMonth() >= fiscalStartMonthIndex
          ? new Date(now.getFullYear() - 1, fiscalStartMonthIndex, 1)
          : new Date(now.getFullYear() - 2, fiscalStartMonthIndex, 1);
      const lastFiscalEnd = new Date(
        lastFiscalStart.getFullYear() + 1,
        fiscalStartMonthIndex,
        0,
      );
      return {
        start_date: lastFiscalStart.toISOString().split('T')[0],
        end_date: lastFiscalEnd.toISOString().split('T')[0],
      };

    case 'fiscal_year_minus_2':
      const fy2Start =
        now.getMonth() >= fiscalStartMonthIndex
          ? new Date(now.getFullYear() - 2, fiscalStartMonthIndex, 1)
          : new Date(now.getFullYear() - 3, fiscalStartMonthIndex, 1);
      const fy2End = new Date(
        fy2Start.getFullYear() + 1,
        fiscalStartMonthIndex,
        0,
      );
      return {
        start_date: fy2Start.toISOString().split('T')[0],
        end_date: fy2End.toISOString().split('T')[0],
      };

    case 'fiscal_year_minus_3':
      const fy3Start =
        now.getMonth() >= fiscalStartMonthIndex
          ? new Date(now.getFullYear() - 3, fiscalStartMonthIndex, 1)
          : new Date(now.getFullYear() - 4, fiscalStartMonthIndex, 1);
      const fy3End = new Date(
        fy3Start.getFullYear() + 1,
        fiscalStartMonthIndex,
        0,
      );
      return {
        start_date: fy3Start.toISOString().split('T')[0],
        end_date: fy3End.toISOString().split('T')[0],
      };

    default:
      return {
        start_date: new Date(now.getFullYear(), now.getMonth(), 1)
          .toISOString()
          .split('T')[0],
        end_date: today.toISOString().split('T')[0],
      };
  }
};

interface ReportFilters {
  client_id: string;
  project_id: string;
  user_id: string;
  start_date: string;
  end_date: string;
  billable: 'all' | 'yes' | 'no';
  invoiced: 'all' | 'yes' | 'no';
  report_type: 'summary' | 'detailed' | 'sred';
}

// SR&ED Report Types
interface SredEligibility {
  overall_eligible: boolean;
  confidence_percentage: number;
  eligible_hours_estimate: number;
  eligible_percentage: number;
  eligibility_level: 'high' | 'medium' | 'low' | 'unlikely';
}

interface TechnologicalUncertainty {
  description: string;
  technical_context?: string;
  resolution_approach: string;
  related_activities: string[];
}

interface TechnologicalAdvancement {
  description: string;
  evidence: string;
  impact: string;
}

interface HypothesisTested {
  hypothesis: string;
  test_methodology: string;
  outcome: string;
  iterations?: number;
}

interface TechnicalResult {
  description: string;
  metrics?: string;
  evidence: string;
  impact: string;
}

interface ActivityClassification {
  activity: string;
  sred_category?: string;
  category?: string;
  technical_complexity?: 'high' | 'medium' | 'low';
  eligible_percentage: number;
  justification: string;
}

interface ClaimRecommendations {
  recommended_claim_hours: number;
  recommended_claim_percentage: number;
  key_points_for_claim?: string[];
  key_technical_points?: string[];
  risks?: string[];
  documentation_gaps?: string[];
  mitigation_strategies?: string[];
  evidence_strength?: 'strong' | 'moderate' | 'weak';
}

interface T661FormLine {
  title: string;
  content: string;
  word_count: number;
}

interface T661FormContent {
  line_242: T661FormLine;
  line_244: T661FormLine;
  line_246: T661FormLine;
}

interface ProjectTechnicalSummary {
  content: string;
  word_count: number;
}

interface CustomModule {
  name: string;
  description: string;
  files: string[];
}

interface TechnologyStack {
  content: string;
  languages: string[];
  frameworks: string[];
  libraries: string[];
  custom_modules: CustomModule[];
  architectural_patterns: string[];
  notable_customizations: string[];
}

interface ProjectAnalysis {
  project_id: number;
  project_name: string;
  client_name: string | null;
  total_hours: number;
  total_entries: number;
  employee_hours: number;
  contractor_hours: number;
  employees: string[];
  contractors: string[];
  date_range: { start: string; end: string };
  activities_summary: Array<{
    task: string;
    hours: number;
    entries_count: number;
    descriptions: string[];
    users: string[];
  }>;
  ai_analysis: {
    sred_eligibility: SredEligibility;
    project_technical_summary?: ProjectTechnicalSummary;
    technology_stack?: TechnologyStack;
    t661_form_content?: T661FormContent;
    technological_uncertainties?: TechnologicalUncertainty[];
    technological_advancements?: TechnologicalAdvancement[];
    hypotheses_tested?: HypothesisTested[];
    technical_results?: TechnicalResult[];
    systematic_investigation?: {
      present: boolean;
      methodology?: string;
      evidence: string;
      improvements_needed: string[];
    };
    documentation_quality?: {
      score: number;
      strengths: string[];
      weaknesses: string[];
      recommendations?: string[];
    };
    activity_classification?: ActivityClassification[];
    claim_recommendations: ClaimRecommendations;
    executive_summary: string;
  } | null;
  error?: string;
  analyzed_at: string;
}

interface ClientSummary {
  total_projects: number;
  total_hours: number;
  employee_hours: number;
  contractor_hours: number;
  estimated_eligible_hours: number;
  estimated_eligible_percentage: number;
}

interface ClientAnalysis {
  client_id: number | null;
  client_name: string;
  projects: ProjectAnalysis[];
  client_summary: ClientSummary | null;
}

interface OverallSummary {
  total_projects_analyzed: number;
  total_hours: number;
  employee_hours: number;
  contractor_hours: number;
  estimated_eligible_hours: number;
  estimated_eligible_percentage: number;
  projects_with_high_eligibility: number;
  projects_with_medium_eligibility: number;
  projects_with_low_eligibility: number;
}

interface SredReportData {
  generated_at: string;
  organization_id: number;
  organization_name: string;
  clients: ClientAnalysis[];
  overall_summary: OverallSummary | null;
}

interface ReportTotals {
  total_hours: number;
  total_billable_amount: number;
  total_cost_amount: number;
  total_entries: number;
}

interface DetailedReportEntry {
  id: number;
  entry_date: string;
  hours_worked: number;
  description: string;
  is_billable: boolean;
  billable_rate: number;
  billable_amount: number;
  cost_amount: number;
  user: {
    id: number;
    full_name: string;
    employment_type:
      | 'employee'
      | 'contractor'
      | 'intern'
      | 'part_time'
      | 'consultant';
    is_contractor: boolean;
  };
  project: {
    id: number;
    name: string;
    client: {
      id: number;
      name: string;
    } | null;
  } | null;
}

interface SummaryUser {
  name: string;
  employment_type:
    | 'employee'
    | 'contractor'
    | 'intern'
    | 'part_time'
    | 'consultant';
  is_contractor: boolean;
  total_hours: number;
  total_billable_amount: number;
  total_cost_amount: number;
}

interface SummaryProject {
  total_hours: number;
  total_billable_amount: number;
  total_cost_amount: number;
  users: SummaryUser[];
}

interface SummaryClient {
  total_hours: number;
  total_billable_amount: number;
  total_cost_amount: number;
  projects: Record<string, SummaryProject>;
}

interface ReportData {
  report_type: 'summary' | 'detailed';
  time_entries?: DetailedReportEntry[];
  summary?: Record<string, SummaryClient>;
  totals: ReportTotals;
}

const TimeEntryReports: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [sredReportData, setSredReportData] = useState<SredReportData | null>(
    null,
  );
  const [expandedClients, setExpandedClients] = useState<Set<string>>(
    new Set(),
  );
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(
    new Set(),
  );
  const [mobileExpandedClients, setMobileExpandedClients] = useState<
    Set<string>
  >(new Set());
  const [mobileExpandedProjects, setMobileExpandedProjects] = useState<
    Set<string>
  >(new Set());
  const [mobileExpandedEntries, setMobileExpandedEntries] = useState<
    Set<string>
  >(new Set());
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<number>>(new Set());
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  // Async SR&ED report state
  const [sredReportId, setSredReportId] = useState<string | null>(null);
  const [sredReportProgress, setSredReportProgress] = useState<number>(0);
  const [sredReportMessage, setSredReportMessage] = useState<string>('');

  // Saved reports state
  const [savedReports, setSavedReports] = useState<SavedSredReport[]>([]);
  const [selectedSavedReportId, setSelectedSavedReportId] = useState<
    number | ''
  >('');
  const [loadingSavedReports, setLoadingSavedReports] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [filters, setFilters] = useState<ReportFilters>(() => {
    // Default to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      client_id: '',
      project_id: '',
      user_id: '',
      start_date: thirtyDaysAgo.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
      billable: 'all',
      invoiced: 'all',
      report_type: 'summary',
    };
  });

  // Filtered projects based on selected client
  const filteredProjects = projects.filter((project) => {
    if (!filters.client_id) return true;
    return project.client_id?.toString() === filters.client_id;
  });

  useEffect(() => {
    fetchInitialData();
    fetchSavedReports();
  }, []);

  const fetchSavedReports = async () => {
    setLoadingSavedReports(true);
    try {
      const response = await sredReportsAPI.getAll();
      setSavedReports(response.data.sred_reports || []);
    } catch {
      // Silently fail - saved reports are not critical
    } finally {
      setLoadingSavedReports(false);
    }
  };

  const loadSavedReport = async (reportId: number) => {
    setLoading(true);
    setError('');
    try {
      const response = await sredReportsAPI.getById(reportId);
      const savedReport = response.data.sred_report;

      // Set the filters from the saved report
      if (savedReport.filters) {
        setFilters((prev) => ({
          ...prev,
          client_id: savedReport.filters.client_id?.toString() || '',
          project_id: savedReport.filters.project_id?.toString() || '',
          start_date: savedReport.filters.start_date || prev.start_date,
          end_date: savedReport.filters.end_date || prev.end_date,
          report_type: 'sred',
        }));
      }

      // Set the report data
      if (savedReport.report_data) {
        setSredReportData(savedReport.report_data);
        // Expand all clients and projects by default
        if (savedReport.report_data?.clients) {
          const clientKeys = savedReport.report_data.clients.map(
            (c: ClientAnalysis) => c.client_id?.toString() || 'internal',
          );
          setExpandedClients(new Set(clientKeys));

          const projectIds = savedReport.report_data.clients.flatMap(
            (c: ClientAnalysis) =>
              c.projects.map((p: ProjectAnalysis) => p.project_id),
          );
          setExpandedProjects(new Set(projectIds));
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('sred.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSavedReportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      setSelectedSavedReportId('');
    } else {
      const reportId = parseInt(value);
      setSelectedSavedReportId(reportId);
      loadSavedReport(reportId);
    }
  };

  const refreshReport = async () => {
    if (!selectedSavedReportId) return;

    setRefreshing(true);
    setError('');

    try {
      const response = await sredReportsAPI.refresh(selectedSavedReportId);
      const { report_id } = response.data;

      // Poll for status
      setSredReportId(report_id);
      setSredReportProgress(0);
      setSredReportMessage(t('sred.analyzing'));
      pollSredReportStatus(report_id);
    } catch (err: any) {
      setError(err.response?.data?.error || t('sred.generateFailed'));
      setRefreshing(false);
    }
  };

  const deleteSavedReport = async (reportId: number) => {
    if (!window.confirm(t('sred.confirmDelete'))) return;

    try {
      await sredReportsAPI.delete(reportId);
      // Clear selection if this was the selected report
      if (selectedSavedReportId === reportId) {
        setSelectedSavedReportId('');
        setSredReportData(null);
      }
      // Refresh the list
      fetchSavedReports();
    } catch {
      setError(t('sred.deleteFailed'));
    }
  };

  const fetchInitialData = async () => {
    try {
      const requests: Promise<any>[] = [
        clientsAPI.getAll(),
        projectsAPI.getAll(),
        usersAPI.getAll(),
      ];

      // Fetch organization settings if user has an organization
      if (user?.organization?.id) {
        requests.push(organizationsAPI.get(user.organization.id));
      }

      const responses = await Promise.all(requests);

      setClients(responses[0].data.clients || []);
      setProjects(responses[1].data.projects || []);
      setUsers(responses[2].data.users || []);

      if (responses[3]) {
        setOrganization(responses[3].data.organization);
      }
    } catch (error: any) {
      setError(t('reports.loadFailed'));
    }
  };

  const handleFilterChange = (key: keyof ReportFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset project when client changes
      ...(key === 'client_id' && value !== prev.client_id
        ? { project_id: '' }
        : {}),
    }));
  };

  // Poll for SR&ED report status
  const pollSredReportStatus = async (reportId: string) => {
    try {
      const response = await timeEntriesAPI.getSredReportStatus(reportId);
      const data = response.data;

      if (data.status === 'completed') {
        setSredReportId(null);
        setLoading(false);
        setRefreshing(false);
        setSredReportProgress(100);
        setSredReportMessage('');

        if (data.error) {
          setError(data.error);
        } else if (data.report) {
          setSredReportData(data.report);
          // Expand all clients and projects by default
          if (data.report?.clients) {
            const clientKeys = data.report.clients.map(
              (c: ClientAnalysis) => c.client_id?.toString() || 'internal',
            );
            setExpandedClients(new Set(clientKeys));

            const projectIds = data.report.clients.flatMap(
              (c: ClientAnalysis) =>
                c.projects.map((p: ProjectAnalysis) => p.project_id),
            );
            setExpandedProjects(new Set(projectIds));
          }
          // Refresh saved reports list to include the new report
          fetchSavedReports();
        }
      } else if (data.status === 'failed') {
        setSredReportId(null);
        setLoading(false);
        setRefreshing(false);
        setSredReportProgress(0);
        setSredReportMessage('');
        setError(data.error || t('reports.generateFailed'));
      } else if (data.status === 'processing' || data.status === 'queued') {
        setSredReportProgress(data.progress || 0);
        setSredReportMessage(data.message || t('sred.analyzing'));
        // Continue polling
        setTimeout(() => pollSredReportStatus(reportId), 2000);
      } else if (data.status === 'not_found') {
        setSredReportId(null);
        setLoading(false);
        setError(t('reports.reportNotFound') || 'Report not found or expired');
      }
    } catch (error: any) {
      setSredReportId(null);
      setLoading(false);
      setSredReportProgress(0);
      setSredReportMessage('');
      setError(error.response?.data?.error || t('reports.generateFailed'));
    }
  };

  const generateReport = async () => {
    setLoading(true);
    setError('');
    setReportData(null);
    setSelectedEntryIds(new Set());
    setSredReportData(null);
    setSredReportId(null);
    setSredReportProgress(0);
    setSredReportMessage('');

    try {
      const params: Record<string, any> = {
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== ''),
        ),
      };

      // Convert string IDs to numbers
      if (params.client_id) params.client_id = parseInt(params.client_id);
      if (params.project_id) params.project_id = parseInt(params.project_id);
      if (params.user_id) params.user_id = parseInt(params.user_id);

      if (filters.report_type === 'sred') {
        // SR&ED report uses async endpoint
        const response = await timeEntriesAPI.getSredReport({
          client_id: params.client_id,
          project_id: params.project_id,
          start_date: params.start_date,
          end_date: params.end_date,
        });

        if (response.data.error && !response.data.report_id) {
          setError(response.data.error);
          setLoading(false);
        } else if (response.data.report_id) {
          // Async report started - begin polling
          setSredReportId(response.data.report_id);
          setSredReportProgress(0);
          setSredReportMessage(response.data.message || t('sred.analyzing'));
          pollSredReportStatus(response.data.report_id);
        } else if (response.data.report) {
          // Immediate response (backward compatibility)
          setSredReportData(response.data.report);
          setLoading(false);
        }
      } else {
        const response = await timeEntriesAPI.getReports(params);
        setReportData(response.data);
        setLoading(false);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || t('reports.generateFailed'));
      setLoading(false);
    }
  };

  const toggleClient = (clientKey: string) => {
    setExpandedClients((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clientKey)) {
        newSet.delete(clientKey);
      } else {
        newSet.add(clientKey);
      }
      return newSet;
    });
  };

  const toggleProject = (projectId: number) => {
    setExpandedProjects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const toggleMobileSet = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    key: string,
  ) => {
    setter((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getEligibilityColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'basic_research':
        return isDark
          ? 'bg-purple-500/20 text-purple-300'
          : 'bg-purple-100 text-purple-800';
      case 'applied_research':
        return isDark
          ? 'bg-blue-500/20 text-blue-300'
          : 'bg-blue-100 text-blue-800';
      case 'experimental_development':
        return isDark
          ? 'bg-emerald-500/20 text-emerald-300'
          : 'bg-emerald-100 text-emerald-800';
      case 'support_work':
        return isDark
          ? 'bg-yellow-500/20 text-yellow-300'
          : 'bg-yellow-100 text-yellow-800';
      default:
        return isDark
          ? 'bg-slate-700 text-slate-300'
          : 'bg-slate-100 text-slate-800';
    }
  };

  const handleDatePreset = (preset: string) => {
    const fiscalYearStartMonth = organization?.fiscal_year_start_month || 1;
    const dateRange = getDateRange(preset, fiscalYearStartMonth);
    setFilters((prev) => ({
      ...prev,
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    }));
    setSelectedPreset(preset);
  };

  const resetFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setFilters({
      client_id: '',
      project_id: '',
      user_id: '',
      start_date: thirtyDaysAgo.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
      billable: 'all',
      invoiced: 'all',
      report_type: 'summary',
    });
    setSelectedPreset(null);
    setReportData(null);
    setSredReportData(null);
    setExpandedClients(new Set());
    setExpandedProjects(new Set());
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    const numAmount = Number(amount || 0);
    return `$${numAmount.toFixed(2)}`;
  };

  // Theme-aware class helpers
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50'
    : 'bg-white border border-slate-200 shadow-sm';
  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';
  const selectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';
  const labelClass = `block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  return (
    <div>
      {/* Sticky Header */}
      <div
        className={`md:sticky md:top-0 z-20 ${isDark ? 'bg-slate-900' : 'bg-slate-50'} pb-4 md:-mx-6 p-6 md:px-6 pt-0`}
      >
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`p-2 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}
              >
                <span
                  className={isDark ? 'text-violet-400' : 'text-violet-600'}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </span>
              </div>
              <h1
                className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('reports.title')}
              </h1>
            </div>
          </div>
        </div>

        {/* Filters Card - Inside Sticky */}
        <div className={`${cardClass} rounded-2xl p-6`}>
          {error && (
            <div
              className={`mb-4 p-3 rounded-xl ${isDark ? 'bg-rose-500/10 border border-rose-500/20 text-rose-400' : 'bg-rose-50 border border-rose-200 text-rose-700'}`}
            >
              {error}
            </div>
          )}

          {/* SR&ED Report Progress */}
          {sredReportId && loading && (
            <div
              className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-200'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <svg
                    className={`animate-spin h-5 w-5 mr-3 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span
                    className={`font-medium ${isDark ? 'text-violet-300' : 'text-violet-800'}`}
                  >
                    {t('sred.generatingReport') || 'Generating SR&ED Report...'}
                  </span>
                </div>
                <span
                  className={`font-bold ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                >
                  {sredReportProgress}%
                </span>
              </div>
              <div
                className={`w-full rounded-full h-2.5 ${isDark ? 'bg-violet-500/20' : 'bg-violet-200'}`}
              >
                <div
                  className="bg-violet-600 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${sredReportProgress}%` }}
                ></div>
              </div>
              {sredReportMessage && (
                <p
                  className={`mt-2 text-sm ${isDark ? 'text-violet-300' : 'text-violet-700'}`}
                >
                  {sredReportMessage}
                </p>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="space-y-6 mb-6">
            {/* First Row - Basic Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Report Type */}
              <div>
                <label className={labelClass}>{t('reports.reportType')}</label>
                <select
                  value={filters.report_type}
                  onChange={(e) =>
                    handleFilterChange('report_type', e.target.value)
                  }
                  className={selectClass}
                >
                  <option value="summary">{t('reports.summary')}</option>
                  <option value="detailed">{t('reports.detailed')}</option>
                  <option value="sred">{t('sred.title')}</option>
                </select>
              </div>

              {/* Saved Reports Dropdown - Only show when SR&ED is selected */}
              {filters.report_type === 'sred' && (
                <div>
                  <label className={labelClass}>{t('sred.savedReports')}</label>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedSavedReportId}
                      onChange={handleSavedReportChange}
                      disabled={
                        loadingSavedReports ||
                        loading ||
                        savedReports.length === 0
                      }
                      className={`${selectClass} flex-1 disabled:opacity-50`}
                    >
                      <option value="">
                        {loadingSavedReports
                          ? t('common.loading')
                          : savedReports.length === 0
                            ? t('sred.noSavedReports')
                            : t('sred.selectSavedReport')}
                      </option>
                      {savedReports.map((report) => (
                        <option key={report.id} value={report.id}>
                          {report.name} (
                          {new Date(report.created_at).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                    {selectedSavedReportId && (
                      <button
                        onClick={() =>
                          deleteSavedReport(selectedSavedReportId as number)
                        }
                        className="p-2 text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all"
                        title={t('sred.deleteReport')}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Client Filter */}
              <div>
                <label className={labelClass}>{t('reports.client')}</label>
                <select
                  value={filters.client_id}
                  onChange={(e) =>
                    handleFilterChange('client_id', e.target.value)
                  }
                  className={selectClass}
                >
                  <option value="">{t('reports.allClients')}</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project Filter */}
              <div>
                <label className={labelClass}>{t('reports.project')}</label>
                <select
                  value={filters.project_id}
                  onChange={(e) =>
                    handleFilterChange('project_id', e.target.value)
                  }
                  className={selectClass}
                  disabled={!filters.client_id}
                >
                  <option value="">{t('reports.allProjects')}</option>
                  {filteredProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* User Filter */}
              <div>
                <label className={labelClass}>{t('reports.user')}</label>
                <select
                  value={filters.user_id}
                  onChange={(e) =>
                    handleFilterChange('user_id', e.target.value)
                  }
                  className={selectClass}
                >
                  <option value="">{t('reports.allUsers')}</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Billable Filter */}
              <div>
                <label className={labelClass}>{t('reports.billable')}</label>
                <select
                  value={filters.billable}
                  onChange={(e) =>
                    handleFilterChange('billable', e.target.value)
                  }
                  className={selectClass}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="yes">{t('common.yes')}</option>
                  <option value="no">{t('common.no')}</option>
                </select>
              </div>
            </div>

            {/* Second Row - Status and Date Filters */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] gap-6">
              {/* Invoiced Filter */}
              <div className="lg:col-span-1">
                <label className={labelClass}>{t('reports.invoiced')}</label>
                <select
                  value={filters.invoiced}
                  onChange={(e) =>
                    handleFilterChange('invoiced', e.target.value)
                  }
                  className={selectClass}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="yes">{t('common.yes')}</option>
                  <option value="no">{t('common.no')}</option>
                </select>
              </div>

              {/* Date Range Picker */}
              <div className="lg:col-span-1">
                <CollapsibleDateRangePicker
                  filters={filters}
                  selectedPreset={selectedPreset}
                  onFiltersChange={setFilters}
                  onPresetChange={setSelectedPreset}
                  onDatePreset={handleDatePreset}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={generateReport}
              disabled={loading || refreshing}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? t('reports.generating') : t('reports.generateReport')}
            </button>

            {/* Refresh Button - Only show when SR&ED is selected and a saved report is loaded */}
            {filters.report_type === 'sred' && selectedSavedReportId && (
              <button
                onClick={refreshReport}
                disabled={loading || refreshing}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200 disabled:opacity-50"
              >
                {refreshing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('sred.refreshing')}
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {t('sred.refresh')}
                  </>
                )}
              </button>
            )}

            <button
              onClick={resetFilters}
              className={`px-5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}
            >
              {t('reports.resetFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className={`${cardClass} rounded-2xl p-6`}>
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
            <h2
              className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {reportData.report_type === 'summary'
                ? t('reports.summaryReport')
                : t('reports.detailedReport')}
            </h2>
            <div className="flex flex-wrap gap-2">
              {/* Bulk Mark/Unmark Invoiced - Only show in detailed view */}
              {reportData.report_type === 'detailed' &&
                reportData.time_entries &&
                reportData.time_entries.length > 0 && (
                  <>
                    {selectedEntryIds.size > 0 && (
                      <span className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-xl ${isDark ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                        {selectedEntryIds.size} selected
                        <button onClick={() => setSelectedEntryIds(new Set())} className="ml-1 opacity-60 hover:opacity-100">✕</button>
                      </span>
                    )}
                    {filters.invoiced !== 'yes' && (
                      <button
                        onClick={async () => {
                          if (
                            !reportData.time_entries ||
                            reportData.time_entries.length === 0
                          )
                            return;
                          try {
                            const entryIds = selectedEntryIds.size > 0
                              ? Array.from(selectedEntryIds)
                              : reportData.time_entries.map((e) => e.id).filter(Boolean) as number[];
                            if (entryIds.length === 0) return;
                            await timeEntriesAPI.bulkMarkInvoiced(
                              entryIds,
                              true,
                            );
                            generateReport();
                          } catch (error) {
                            console.error(
                              'Failed to mark entries as invoiced:',
                              error,
                            );
                          }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200"
                        title={selectedEntryIds.size > 0 ? `Mark ${selectedEntryIds.size} selected as invoiced` : 'Mark all shown entries as invoiced'}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {selectedEntryIds.size > 0 ? `Mark ${selectedEntryIds.size} as invoiced` : (t('reports.markInvoiced') || 'Mark Invoiced')}
                      </button>
                    )}
                    {filters.invoiced !== 'no' && user?.role === 'admin' && (
                      <button
                        onClick={async () => {
                          if (
                            !reportData.time_entries ||
                            reportData.time_entries.length === 0
                          )
                            return;
                          try {
                            const entryIds = selectedEntryIds.size > 0
                              ? Array.from(selectedEntryIds)
                              : reportData.time_entries.map((e) => e.id).filter(Boolean) as number[];
                            if (entryIds.length === 0) return;
                            await timeEntriesAPI.bulkMarkInvoiced(
                              entryIds,
                              false,
                            );
                            generateReport();
                          } catch (error) {
                            console.error(
                              'Failed to unmark entries as invoiced:',
                              error,
                            );
                            showToast('Failed to unmark entries as invoiced', 'error');
                          }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-amber-500/25 transition-all duration-200"
                        title={selectedEntryIds.size > 0 ? `Unmark ${selectedEntryIds.size} selected` : 'Mark all shown entries as not invoiced'}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                        {t('reports.unmarkInvoiced') || 'Unmark Invoiced'}
                      </button>
                    )}
                  </>
                )}
              <button
                onClick={() => {
                  if (
                    reportData.report_type === 'summary' &&
                    reportData.summary
                  ) {
                    exportSummaryToExcel(reportData as any, t, {
                      start_date: filters.start_date,
                      end_date: filters.end_date,
                    });
                  } else if (
                    reportData.report_type === 'detailed' &&
                    reportData.time_entries
                  ) {
                    exportDetailedToExcel(reportData as any, t, {
                      start_date: filters.start_date,
                      end_date: filters.end_date,
                    });
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {t('reports.exportExcel')}
              </button>
            </div>
          </div>

          {/* Totals */}
          <div
            className={`grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}
          >
            <div className="text-center">
              <div
                className={`text-lg sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {reportData.totals?.total_hours || 0}
              </div>
              <div
                className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
              >
                {t('reports.totalHours')}
              </div>
            </div>
            <div className="text-center">
              <div
                className={`text-lg sm:text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
              >
                {formatCurrency(reportData.totals?.total_billable_amount)}
              </div>
              <div
                className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
              >
                {t('reports.billableAmount')}
              </div>
            </div>
            <div className="text-center">
              <div
                className={`text-lg sm:text-2xl font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}
              >
                {formatCurrency(reportData.totals?.total_cost_amount)}
              </div>
              <div
                className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
              >
                {t('reports.costAmount')}
              </div>
            </div>
            <div className="text-center">
              <div
                className={`text-lg sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {reportData.totals?.total_entries || 0}
              </div>
              <div
                className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
              >
                {t('reports.entries')}
              </div>
            </div>
          </div>

          {/* Summary View */}
          {reportData.report_type === 'summary' && reportData.summary && (
            <>
              {/* Mobile Card View */}
              <div className="lg:hidden space-y-3">
                {Object.entries(reportData.summary).map(
                  ([clientName, clientData]) => {
                    const isClientExpanded =
                      mobileExpandedClients.has(clientName);
                    return (
                      <div
                        key={clientName}
                        className={`rounded-xl overflow-hidden ${isDark ? 'bg-slate-800/60 border border-slate-700/50' : 'bg-white border border-slate-200 shadow-sm'}`}
                      >
                        {/* Client Header */}
                        <button
                          onClick={() =>
                            toggleMobileSet(
                              setMobileExpandedClients,
                              clientName,
                            )
                          }
                          className={`w-full flex items-center justify-between px-4 py-3 ${isDark ? 'bg-violet-500/10' : 'bg-violet-50'}`}
                        >
                          <div className="flex-1 text-left">
                            <div
                              className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                            >
                              {clientName}
                            </div>
                            <div
                              className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                            >
                              {Number(clientData.total_hours || 0).toFixed(1)}h
                              <span
                                className={`ml-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                              >
                                {formatCurrency(
                                  clientData.total_billable_amount,
                                )}
                              </span>
                              <span
                                className={`ml-2 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}
                              >
                                {formatCurrency(clientData.total_cost_amount)}
                              </span>
                            </div>
                          </div>
                          <svg
                            className={`w-5 h-5 flex-shrink-0 transition-transform ${isClientExpanded ? 'rotate-180' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        {/* Projects */}
                        {isClientExpanded && (
                          <div
                            className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-100'}`}
                          >
                            {Object.entries(clientData.projects || {}).map(
                              ([projectName, projectData]) => {
                                const projectKey = `${clientName}-${projectName}`;
                                const isProjectExpanded =
                                  mobileExpandedProjects.has(projectKey);
                                return (
                                  <div key={projectKey}>
                                    {/* Project Header */}
                                    <button
                                      onClick={() =>
                                        toggleMobileSet(
                                          setMobileExpandedProjects,
                                          projectKey,
                                        )
                                      }
                                      className={`w-full flex items-center justify-between px-4 py-2.5 ${isDark ? 'bg-emerald-500/5' : 'bg-emerald-50/50'}`}
                                    >
                                      <div className="flex-1 text-left pl-2">
                                        <div
                                          className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                                        >
                                          {projectName}
                                        </div>
                                        <div
                                          className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                        >
                                          {Number(
                                            projectData.total_hours || 0,
                                          ).toFixed(1)}
                                          h
                                          <span
                                            className={`ml-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                                          >
                                            {formatCurrency(
                                              projectData.total_billable_amount,
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                      <svg
                                        className={`w-4 h-4 flex-shrink-0 transition-transform ${isProjectExpanded ? 'rotate-180' : ''} ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 9l-7 7-7-7"
                                        />
                                      </svg>
                                    </button>
                                    {/* Users */}
                                    {isProjectExpanded && (
                                      <div
                                        className={`px-4 py-2 space-y-2 ${isDark ? 'bg-slate-800/30' : 'bg-slate-50/50'}`}
                                      >
                                        {(projectData.users || []).length ===
                                        0 ? (
                                          <div
                                            className={`text-sm italic pl-4 py-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                                          >
                                            {t('reports.noUsersAssigned')}
                                          </div>
                                        ) : (
                                          projectData.users.map((user, idx) => (
                                            <div
                                              key={idx}
                                              className={`flex items-center justify-between pl-4 py-1.5 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-white'}`}
                                            >
                                              <div className="flex items-center gap-2 min-w-0">
                                                <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                                  <span className="text-[10px] font-bold text-white">
                                                    {user.name
                                                      .split(' ')
                                                      .map((n) => n[0])
                                                      .join('')
                                                      .substring(0, 2)
                                                      .toUpperCase()}
                                                  </span>
                                                </div>
                                                <div className="min-w-0">
                                                  <div
                                                    className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                                                  >
                                                    {user.name}
                                                    {user.is_contractor && (
                                                      <span
                                                        className={`ml-1.5 px-1 py-0.5 text-[10px] font-medium rounded ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-800'}`}
                                                      >
                                                        {t(
                                                          'reports.contractor',
                                                        )}
                                                      </span>
                                                    )}
                                                  </div>
                                                  <div
                                                    className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                                  >
                                                    {Number(
                                                      user.total_hours || 0,
                                                    ).toFixed(1)}
                                                    h
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="flex flex-col items-end gap-0.5 flex-shrink-0 pl-2">
                                                <span
                                                  className={`text-xs font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                                                >
                                                  {formatCurrency(
                                                    user.total_billable_amount,
                                                  )}
                                                </span>
                                                <span
                                                  className={`text-xs font-semibold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}
                                                >
                                                  {formatCurrency(
                                                    user.total_cost_amount,
                                                  )}
                                                </span>
                                              </div>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              },
                            )}
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
                {/* Mobile Grand Total */}
                <div
                  className={`rounded-xl p-4 ${isDark ? 'bg-slate-950 border border-slate-700' : 'bg-slate-800'}`}
                >
                  <div className="text-sm font-bold text-white text-center mb-2">
                    {t('reports.grandTotal')}
                  </div>
                  <div className="flex justify-around">
                    <div className="text-center">
                      <div className="text-sm font-bold text-white">
                        {Number(reportData.totals.total_hours || 0).toFixed(1)}h
                      </div>
                      <div className="text-xs text-slate-400">
                        {t('reports.hours')}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-green-300">
                        {formatCurrency(
                          reportData.totals.total_billable_amount,
                        )}
                      </div>
                      <div className="text-xs text-slate-400">
                        {t('reports.billableAmount')}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-orange-300">
                        {formatCurrency(reportData.totals.total_cost_amount)}
                      </div>
                      <div className="text-xs text-slate-400">
                        {t('reports.costAmount')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table
                  className={`min-w-full divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-200'}`}
                >
                  <thead className={isDark ? 'bg-slate-800/80' : 'bg-slate-50'}>
                    <tr>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.client')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.project')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.user')}
                      </th>
                      <th
                        className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.hours')}
                      </th>
                      <th
                        className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.billableAmount')}
                      </th>
                      <th
                        className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.costAmount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-200'}`}
                  >
                    {Object.entries(reportData.summary)
                      .map(([clientName, clientData]) => {
                        const clientRows: React.ReactElement[] = [];
                        let clientRowIndex = 0;

                        // Calculate total client projects and users for rowspan
                        const totalClientRows = Object.entries(
                          clientData.projects || {},
                        ).reduce((total, [_, projectData]) => {
                          return (
                            total +
                            Math.max(1, (projectData.users || []).length)
                          );
                        }, 0);

                        Object.entries(clientData.projects || {}).forEach(
                          ([projectName, projectData], projectIndex) => {
                            const users = projectData.users || [];
                            const projectRows = Math.max(1, users.length);

                            if (users.length === 0) {
                              // Project with no users
                              clientRows.push(
                                <tr
                                  key={`${clientName}-${projectName}`}
                                  className={`transition-colors ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'}`}
                                >
                                  {clientRowIndex === 0 && (
                                    <td
                                      rowSpan={totalClientRows}
                                      className={`px-6 py-4 text-sm font-semibold align-top border-r-2 ${isDark ? 'text-white bg-violet-500/10 border-violet-500/30' : 'text-slate-900 bg-violet-50 border-violet-200'}`}
                                    >
                                      <div className="space-y-1">
                                        <div className="text-base">
                                          {clientName}
                                        </div>
                                        <div
                                          className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                        >
                                          {Number(
                                            clientData.total_hours || 0,
                                          ).toFixed(1)}
                                          h •{' '}
                                          {formatCurrency(
                                            clientData.total_billable_amount,
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  )}
                                  <td
                                    className={`px-6 py-4 text-sm font-medium ${isDark ? 'text-white bg-emerald-500/10' : 'text-slate-900 bg-emerald-50'}`}
                                  >
                                    <div className="space-y-1">
                                      <div>{projectName}</div>
                                      <div
                                        className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                      >
                                        {Number(
                                          projectData.total_hours || 0,
                                        ).toFixed(1)}
                                        h •{' '}
                                        {formatCurrency(
                                          projectData.total_billable_amount,
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td
                                    className={`px-6 py-4 text-sm italic ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                                  >
                                    {t('reports.noUsersAssigned')}
                                  </td>
                                  <td
                                    className={`px-6 py-4 text-sm text-center font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                                  >
                                    {Number(
                                      projectData.total_hours || 0,
                                    ).toFixed(1)}
                                  </td>
                                  <td
                                    className={`px-6 py-4 text-sm text-center font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                                  >
                                    {formatCurrency(
                                      projectData.total_billable_amount,
                                    )}
                                  </td>
                                  <td
                                    className={`px-6 py-4 text-sm text-center font-semibold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}
                                  >
                                    {formatCurrency(
                                      projectData.total_cost_amount,
                                    )}
                                  </td>
                                </tr>,
                              );
                              clientRowIndex++;
                            } else {
                              // Project with users
                              users.forEach((user, userIndex) => {
                                clientRows.push(
                                  <tr
                                    key={`${clientName}-${projectName}-${userIndex}`}
                                    className={`transition-colors ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'}`}
                                  >
                                    {clientRowIndex === 0 && (
                                      <td
                                        rowSpan={totalClientRows}
                                        className={`px-6 py-4 text-sm font-semibold align-top border-r-2 ${isDark ? 'text-white bg-violet-500/10 border-violet-500/30' : 'text-slate-900 bg-violet-50 border-violet-200'}`}
                                      >
                                        <div className="space-y-2">
                                          <div className="text-base">
                                            {clientName}
                                          </div>
                                          <div
                                            className={`text-xs space-y-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                          >
                                            <div className="flex items-center">
                                              <svg
                                                className="w-3 h-3 mr-1 text-blue-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                              </svg>
                                              {Number(
                                                clientData.total_hours || 0,
                                              ).toFixed(1)}{' '}
                                              hours
                                            </div>
                                            <div className="flex items-center">
                                              <svg
                                                className="w-3 h-3 mr-1 text-green-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                                                />
                                              </svg>
                                              {formatCurrency(
                                                clientData.total_billable_amount,
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    )}
                                    {userIndex === 0 && (
                                      <td
                                        rowSpan={projectRows}
                                        className={`px-6 py-4 text-sm font-medium align-top border-r ${isDark ? 'text-white bg-emerald-500/10 border-emerald-500/30' : 'text-slate-900 bg-emerald-50 border-emerald-200'}`}
                                      >
                                        <div className="space-y-2">
                                          <div className="text-sm">
                                            {projectName}
                                          </div>
                                          <div
                                            className={`text-xs space-y-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                          >
                                            <div className="flex items-center">
                                              <svg
                                                className="w-3 h-3 mr-1 text-blue-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                              </svg>
                                              {Number(
                                                projectData.total_hours || 0,
                                              ).toFixed(1)}{' '}
                                              hours
                                            </div>
                                            <div className="flex items-center">
                                              <svg
                                                className="w-3 h-3 mr-1 text-green-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                                                />
                                              </svg>
                                              {formatCurrency(
                                                projectData.total_billable_amount,
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    )}
                                    <td
                                      className={`px-6 py-4 text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}
                                    >
                                      <div className="flex items-center">
                                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                                          <span className="text-xs font-bold text-white">
                                            {user.name
                                              .split(' ')
                                              .map((n) => n[0])
                                              .join('')
                                              .substring(0, 2)
                                              .toUpperCase()}
                                          </span>
                                        </div>
                                        <div>
                                          <div className="font-medium flex items-center gap-2">
                                            {user.name}
                                            {user.is_contractor && (
                                              <span
                                                className={`px-1.5 py-0.5 text-xs font-medium rounded ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-800'}`}
                                              >
                                                {t('reports.contractor')}
                                              </span>
                                            )}
                                          </div>
                                          <div
                                            className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                          >
                                            {Number(
                                              user.total_hours || 0,
                                            ).toFixed(1)}
                                            h logged
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td
                                      className={`px-6 py-4 text-sm text-center font-medium ${isDark ? 'text-white bg-slate-800/50' : 'text-slate-900 bg-slate-50'}`}
                                    >
                                      <div
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-800'}`}
                                      >
                                        {Number(user.total_hours || 0).toFixed(
                                          1,
                                        )}
                                        h
                                      </div>
                                    </td>
                                    <td
                                      className={`px-6 py-4 text-sm text-center font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                                    >
                                      <div
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-800'}`}
                                      >
                                        {formatCurrency(
                                          user.total_billable_amount,
                                        )}
                                      </div>
                                    </td>
                                    <td
                                      className={`px-6 py-4 text-sm text-center font-semibold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}
                                    >
                                      <div
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-800'}`}
                                      >
                                        {formatCurrency(user.total_cost_amount)}
                                      </div>
                                    </td>
                                  </tr>,
                                );
                                clientRowIndex++;
                              });
                            }
                          },
                        );

                        // Calculate user totals across all projects for this client
                        const userTotalsMap: Record<
                          string,
                          {
                            hours: number;
                            billable: number;
                            cost: number;
                            isContractor: boolean;
                          }
                        > = {};
                        Object.entries(clientData.projects).forEach(
                          ([_, projectData]) => {
                            const users = projectData.users || [];
                            users.forEach((user) => {
                              if (!userTotalsMap[user.name]) {
                                userTotalsMap[user.name] = {
                                  hours: 0,
                                  billable: 0,
                                  cost: 0,
                                  isContractor: user.is_contractor,
                                };
                              }
                              userTotalsMap[user.name].hours +=
                                Number(user.total_hours) || 0;
                              userTotalsMap[user.name].billable +=
                                Number(user.total_billable_amount) || 0;
                              userTotalsMap[user.name].cost +=
                                Number(user.total_cost_amount) || 0;
                            });
                          },
                        );

                        // Add user subtotal rows
                        Object.entries(userTotalsMap).forEach(
                          ([userName, userTotals]) => {
                            clientRows.push(
                              <tr
                                key={`${clientName}-user-${userName}`}
                                className={
                                  isDark
                                    ? 'bg-purple-500/10 border-t border-purple-500/30'
                                    : 'bg-purple-50 border-t border-purple-200'
                                }
                              >
                                <td
                                  colSpan={3}
                                  className={`px-6 py-2 text-sm font-medium text-right ${isDark ? 'text-purple-300' : 'text-purple-800'}`}
                                >
                                  <span className="flex items-center justify-end gap-2">
                                    {userName}
                                    {userTotals.isContractor && (
                                      <span
                                        className={`px-1.5 py-0.5 text-xs font-medium rounded ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-800'}`}
                                      >
                                        {t('reports.contractor')}
                                      </span>
                                    )}
                                  </span>
                                </td>
                                <td
                                  className={`px-6 py-2 text-sm text-center font-medium ${isDark ? 'text-purple-300' : 'text-purple-800'}`}
                                >
                                  {Number(userTotals.hours).toFixed(1)}h
                                </td>
                                <td
                                  className={`px-6 py-2 text-sm text-center font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}
                                >
                                  {formatCurrency(userTotals.billable)}
                                </td>
                                <td
                                  className={`px-6 py-2 text-sm text-center font-medium ${isDark ? 'text-orange-400' : 'text-orange-700'}`}
                                >
                                  {formatCurrency(userTotals.cost)}
                                </td>
                              </tr>,
                            );
                          },
                        );

                        // Add client subtotal row
                        clientRows.push(
                          <tr
                            key={`${clientName}-subtotal`}
                            className={`font-semibold border-t-2 ${isDark ? 'bg-violet-500/20 border-violet-500/40' : 'bg-violet-100 border-violet-300'}`}
                          >
                            <td
                              colSpan={3}
                              className={`px-6 py-3 text-sm text-right ${isDark ? 'text-violet-300' : 'text-violet-900'}`}
                            >
                              {t('reports.clientTotal')}: {clientName}
                            </td>
                            <td
                              className={`px-6 py-3 text-sm text-center ${isDark ? 'text-violet-300' : 'text-violet-900'}`}
                            >
                              {Number(clientData.total_hours || 0).toFixed(1)}h
                            </td>
                            <td
                              className={`px-6 py-3 text-sm text-center ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}
                            >
                              {formatCurrency(clientData.total_billable_amount)}
                            </td>
                            <td
                              className={`px-6 py-3 text-sm text-center ${isDark ? 'text-orange-400' : 'text-orange-700'}`}
                            >
                              {formatCurrency(clientData.total_cost_amount)}
                            </td>
                          </tr>,
                        );

                        return clientRows;
                      })
                      .flat()}
                  </tbody>
                  {/* Grand Total Footer */}
                  <tfoot
                    className={
                      isDark
                        ? 'bg-slate-950 text-white'
                        : 'bg-slate-800 text-white'
                    }
                  >
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-4 text-sm font-bold text-right"
                      >
                        {t('reports.grandTotal')}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-center">
                        {Number(reportData.totals.total_hours || 0).toFixed(1)}h
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-center text-green-300">
                        {formatCurrency(
                          reportData.totals.total_billable_amount,
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-center text-orange-300">
                        {formatCurrency(reportData.totals.total_cost_amount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {/* Detailed View */}
          {reportData.report_type === 'detailed' && reportData.time_entries && (
            <>
              {/* Mobile Card View */}
              <div className="lg:hidden space-y-3">
                {(() => {
                  // Group entries by client for mobile
                  const entriesByClient = reportData.time_entries!.reduce(
                    (acc, entry) => {
                      const clientName =
                        entry.project?.client?.name || t('reports.noClient');
                      if (!acc[clientName]) acc[clientName] = [];
                      acc[clientName].push(entry);
                      return acc;
                    },
                    {} as Record<string, DetailedReportEntry[]>,
                  );

                  return Object.entries(entriesByClient).map(
                    ([clientName, entries]) => {
                      const isClientExpanded = mobileExpandedClients.has(
                        `detail-${clientName}`,
                      );
                      const clientTotals = entries.reduce(
                        (totals, entry) => ({
                          hours: totals.hours + (Number(entry.hours_worked) || 0),
                          billable:
                            totals.billable + (Number(entry.billable_amount) || 0),
                          cost: totals.cost + (Number(entry.cost_amount) || 0),
                        }),
                        { hours: 0, billable: 0, cost: 0 },
                      );

                      return (
                        <div
                          key={clientName}
                          className={`rounded-xl overflow-hidden ${isDark ? 'bg-slate-800/60 border border-slate-700/50' : 'bg-white border border-slate-200 shadow-sm'}`}
                        >
                          {/* Client Header */}
                          <button
                            onClick={() =>
                              toggleMobileSet(
                                setMobileExpandedClients,
                                `detail-${clientName}`,
                              )
                            }
                            className={`w-full flex items-center justify-between px-4 py-3 ${isDark ? 'bg-violet-500/10' : 'bg-violet-50'}`}
                          >
                            <div className="flex-1 text-left">
                              <div
                                className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
                              >
                                {clientName}
                              </div>
                              <div
                                className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                              >
                                {entries.length}{' '}
                                {t('reports.entries').toLowerCase()} ·{' '}
                                {Number(clientTotals.hours).toFixed(1)}h
                                <span
                                  className={`ml-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                                >
                                  {formatCurrency(clientTotals.billable)}
                                </span>
                              </div>
                            </div>
                            <svg
                              className={`w-5 h-5 flex-shrink-0 transition-transform ${isClientExpanded ? 'rotate-180' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          {/* Entries */}
                          {isClientExpanded && (
                            <div
                              className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-100'}`}
                            >
                              {entries.map((entry) => {
                                const entryKey = `entry-${entry.id}`;
                                const isEntryExpanded =
                                  mobileExpandedEntries.has(entryKey);
                                return (
                                  <button
                                    key={entry.id}
                                    onClick={() =>
                                      toggleMobileSet(
                                        setMobileExpandedEntries,
                                        entryKey,
                                      )
                                    }
                                    className={`w-full text-left px-4 py-2.5 ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <div
                                          className={`text-xs flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                        >
                                          {format(
                                            parseISO(entry.entry_date),
                                            'M/d',
                                          )}
                                        </div>
                                        <div
                                          className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                                        >
                                          {entry.user.full_name}
                                        </div>
                                        {entry.user.is_contractor && (
                                          <span
                                            className={`px-1 py-0.5 text-[10px] font-medium rounded flex-shrink-0 ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-800'}`}
                                          >
                                            {t('reports.contractor')}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3 flex-shrink-0 pl-2">
                                        <span
                                          className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                                        >
                                          {entry.hours_worked}h
                                        </span>
                                        <span
                                          className={`text-xs font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                                        >
                                          {formatCurrency(
                                            entry.billable_amount,
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                    {isEntryExpanded && (
                                      <div
                                        className={`mt-2 pt-2 text-xs space-y-1 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
                                      >
                                        <div className="flex justify-between">
                                          <span
                                            className={
                                              isDark
                                                ? 'text-slate-400'
                                                : 'text-slate-500'
                                            }
                                          >
                                            {t('reports.project')}
                                          </span>
                                          <span
                                            className={
                                              isDark
                                                ? 'text-slate-200'
                                                : 'text-slate-900'
                                            }
                                          >
                                            {entry.project?.name ||
                                              t('reports.noProject')}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span
                                            className={
                                              isDark
                                                ? 'text-slate-400'
                                                : 'text-slate-500'
                                            }
                                          >
                                            {t('reports.billable')}
                                          </span>
                                          <span
                                            className={
                                              entry.is_billable
                                                ? isDark
                                                  ? 'text-emerald-400'
                                                  : 'text-emerald-600'
                                                : isDark
                                                  ? 'text-slate-400'
                                                  : 'text-slate-500'
                                            }
                                          >
                                            {entry.is_billable
                                              ? t('common.yes')
                                              : t('common.no')}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span
                                            className={
                                              isDark
                                                ? 'text-slate-400'
                                                : 'text-slate-500'
                                            }
                                          >
                                            {t('reports.costAmount')}
                                          </span>
                                          <span
                                            className={
                                              isDark
                                                ? 'text-orange-400'
                                                : 'text-orange-600'
                                            }
                                          >
                                            {formatCurrency(entry.cost_amount)}
                                          </span>
                                        </div>
                                        {entry.description && (
                                          <div
                                            className={`pt-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                                          >
                                            {entry.description}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    },
                  );
                })()}
                {/* Mobile Grand Total */}
                <div
                  className={`rounded-xl p-4 ${isDark ? 'bg-slate-950 border border-slate-700' : 'bg-slate-800'}`}
                >
                  <div className="text-sm font-bold text-white text-center mb-2">
                    {t('reports.grandTotal')}
                  </div>
                  <div className="flex justify-around">
                    <div className="text-center">
                      <div className="text-sm font-bold text-white">
                        {Number(reportData.totals.total_hours || 0).toFixed(1)}h
                      </div>
                      <div className="text-xs text-slate-400">
                        {t('reports.hours')}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-green-300">
                        {formatCurrency(
                          reportData.totals.total_billable_amount,
                        )}
                      </div>
                      <div className="text-xs text-slate-400">
                        {t('reports.billableAmount')}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-orange-300">
                        {formatCurrency(reportData.totals.total_cost_amount)}
                      </div>
                      <div className="text-xs text-slate-400">
                        {t('reports.costAmount')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table
                  className={`min-w-full divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-200'}`}
                >
                  <thead className={isDark ? 'bg-slate-800/80' : 'bg-slate-50'}>
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          className="rounded border-slate-400 text-blue-600 focus:ring-blue-500"
                          checked={
                            reportData.time_entries.length > 0 &&
                            reportData.time_entries.every((e) => e.id != null && selectedEntryIds.has(e.id as number))
                          }
                          onChange={(ev) => {
                            if (ev.target.checked) {
                              setSelectedEntryIds(new Set((reportData?.time_entries ?? []).map((e) => e.id as number).filter(Boolean)));
                            } else {
                              setSelectedEntryIds(new Set());
                            }
                          }}
                        />
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.date')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.user')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.client')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.project')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.hours')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.billable')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.billAmount')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.costAmount')}
                      </th>
                      <th
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('reports.description')}
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-200'}`}
                  >
                    {(() => {
                      // Group entries by client
                      const entriesByClient = reportData.time_entries.reduce(
                        (acc, entry) => {
                          const clientName =
                            entry.project?.client?.name ||
                            t('reports.noClient');
                          if (!acc[clientName]) {
                            acc[clientName] = [];
                          }
                          acc[clientName].push(entry);
                          return acc;
                        },
                        {} as Record<string, DetailedReportEntry[]>,
                      );

                      const rows: React.ReactElement[] = [];

                      Object.entries(entriesByClient).forEach(
                        ([clientName, entries]) => {
                          // Calculate client totals
                          const clientTotals = entries.reduce(
                            (totals, entry) => ({
                              hours: totals.hours + (Number(entry.hours_worked) || 0),
                              billable:
                                totals.billable + (Number(entry.billable_amount) || 0),
                              cost: totals.cost + (Number(entry.cost_amount) || 0),
                            }),
                            { hours: 0, billable: 0, cost: 0 },
                          );

                          // Group entries by user within client
                          const entriesByUser = entries.reduce(
                            (acc, entry) => {
                              const userName = entry.user.full_name;
                              if (!acc[userName]) {
                                acc[userName] = {
                                  entries: [],
                                  isContractor: entry.user.is_contractor,
                                };
                              }
                              acc[userName].entries.push(entry);
                              return acc;
                            },
                            {} as Record<
                              string,
                              {
                                entries: DetailedReportEntry[];
                                isContractor: boolean;
                              }
                            >,
                          );

                          // Client header row
                          rows.push(
                            <tr
                              key={`client-header-${clientName}`}
                              className={
                                isDark
                                  ? 'bg-violet-500/10 border-t-2 border-violet-500/30'
                                  : 'bg-violet-50 border-t-2 border-violet-200'
                              }
                            >
                              <td
                                colSpan={10}
                                className={`px-6 py-2 text-sm font-bold ${isDark ? 'text-violet-300' : 'text-violet-900'}`}
                              >
                                {t('reports.client')}: {clientName}
                              </td>
                            </tr>,
                          );

                          // Add entries grouped by user
                          Object.entries(entriesByUser).forEach(
                            ([userName, userData]) => {
                              // Calculate user totals
                              const userTotals = userData.entries.reduce(
                                (totals, entry) => ({
                                  hours:
                                    totals.hours + (Number(entry.hours_worked) || 0),
                                  billable:
                                    totals.billable +
                                    (Number(entry.billable_amount) || 0),
                                  cost: totals.cost + (Number(entry.cost_amount) || 0),
                                }),
                                { hours: 0, billable: 0, cost: 0 },
                              );

                              // Add each entry
                              userData.entries.forEach((entry) => {
                                rows.push(
                                  <tr
                                    key={entry.id}
                                    className={
                                      isDark
                                        ? `hover:bg-slate-800/50 ${entry.id != null && selectedEntryIds.has(entry.id as number) ? 'bg-blue-500/10' : ''}`
                                        : `hover:bg-slate-50 ${entry.id != null && selectedEntryIds.has(entry.id as number) ? 'bg-blue-50' : ''}`
                                    }
                                  >
                                    <td className="px-4 py-4 w-10">
                                      <input
                                        type="checkbox"
                                        className="rounded border-slate-400 text-blue-600 focus:ring-blue-500"
                                        checked={entry.id != null && selectedEntryIds.has(entry.id as number)}
                                        onChange={() => {
                                          if (entry.id == null) return;
                                          setSelectedEntryIds(prev => {
                                            const next = new Set(prev);
                                            if (next.has(entry.id as number)) next.delete(entry.id as number);
                                            else next.add(entry.id as number);
                                            return next;
                                          });
                                        }}
                                      />
                                    </td>
                                    <td
                                      className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}
                                    >
                                      {format(
                                        parseISO(entry.entry_date),
                                        'M/d/yyyy',
                                      )}
                                    </td>
                                    <td
                                      className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {entry.user.full_name}
                                        {entry.user.is_contractor && (
                                          <span
                                            className={`px-1.5 py-0.5 text-xs font-medium rounded ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-800'}`}
                                          >
                                            {t('reports.contractor')}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td
                                      className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}
                                    >
                                      {entry.project?.client?.name ||
                                        t('reports.noClient')}
                                    </td>
                                    <td
                                      className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}
                                    >
                                      {entry.project?.name ||
                                        t('reports.noProject')}
                                    </td>
                                    <td
                                      className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}
                                    >
                                      {entry.hours_worked || 0}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span
                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          entry.is_billable
                                            ? isDark
                                              ? 'bg-emerald-500/20 text-emerald-300'
                                              : 'bg-emerald-100 text-emerald-800'
                                            : isDark
                                              ? 'bg-slate-700 text-slate-300'
                                              : 'bg-slate-100 text-slate-800'
                                        }`}
                                      >
                                        {entry.is_billable
                                          ? t('common.yes')
                                          : t('common.no')}
                                      </span>
                                    </td>
                                    <td
                                      className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
                                    >
                                      {formatCurrency(entry.billable_amount)}
                                    </td>
                                    <td
                                      className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-orange-400' : 'text-orange-600'}`}
                                    >
                                      {formatCurrency(entry.cost_amount)}
                                    </td>
                                    <td
                                      className={`px-6 py-4 text-sm max-w-xs truncate ${isDark ? 'text-slate-200' : 'text-slate-900'}`}
                                      title={entry.description}
                                    >
                                      {entry.description}
                                    </td>
                                  </tr>,
                                );
                              });

                              // User subtotal row
                              rows.push(
                                <tr
                                  key={`user-subtotal-${clientName}-${userName}`}
                                  className={
                                    isDark
                                      ? 'bg-purple-500/10 border-t border-purple-500/30'
                                      : 'bg-purple-50 border-t border-purple-200'
                                  }
                                >
                                  <td
                                    colSpan={5}
                                    className={`px-6 py-2 text-sm font-medium text-right ${isDark ? 'text-purple-300' : 'text-purple-800'}`}
                                  >
                                    {userName}
                                    {userData.isContractor && (
                                      <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                                        {t('reports.contractor')}
                                      </span>
                                    )}
                                  </td>
                                  <td
                                    className={`px-6 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-purple-300' : 'text-purple-800'}`}
                                  >
                                    {Number(userTotals.hours).toFixed(1)}h
                                  </td>
                                  <td className="px-6 py-2"></td>
                                  <td
                                    className={`px-6 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}
                                  >
                                    {formatCurrency(userTotals.billable)}
                                  </td>
                                  <td
                                    className={`px-6 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-orange-400' : 'text-orange-700'}`}
                                  >
                                    {formatCurrency(userTotals.cost)}
                                  </td>
                                  <td className="px-6 py-2"></td>
                                </tr>,
                              );
                            },
                          );

                          // Client subtotal row
                          rows.push(
                            <tr
                              key={`client-subtotal-${clientName}`}
                              className={
                                isDark
                                  ? 'bg-violet-500/20 border-t-2 border-violet-500/40'
                                  : 'bg-violet-100 border-t-2 border-violet-300'
                              }
                            >
                              <td
                                colSpan={5}
                                className={`px-6 py-3 text-sm font-bold text-right ${isDark ? 'text-violet-300' : 'text-violet-900'}`}
                              >
                                {t('reports.clientTotal')}: {clientName}
                              </td>
                              <td
                                className={`px-6 py-3 whitespace-nowrap text-sm font-bold ${isDark ? 'text-violet-300' : 'text-violet-900'}`}
                              >
                                {Number(clientTotals.hours).toFixed(1)}h
                              </td>
                              <td className="px-6 py-3"></td>
                              <td
                                className={`px-6 py-3 whitespace-nowrap text-sm font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}
                              >
                                {formatCurrency(clientTotals.billable)}
                              </td>
                              <td
                                className={`px-6 py-3 whitespace-nowrap text-sm font-bold ${isDark ? 'text-orange-400' : 'text-orange-700'}`}
                              >
                                {formatCurrency(clientTotals.cost)}
                              </td>
                              <td className="px-6 py-3"></td>
                            </tr>,
                          );
                        },
                      );

                      return rows;
                    })()}
                  </tbody>
                  {/* Grand Total Footer */}
                  <tfoot
                    className={
                      isDark
                        ? 'bg-slate-950 text-white'
                        : 'bg-slate-800 text-white'
                    }
                  >
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-4 text-sm font-bold text-right"
                      >
                        {t('reports.grandTotal')}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold">
                        {Number(reportData.totals.total_hours || 0).toFixed(1)}h
                      </td>
                      <td className="px-6 py-4"></td>
                      <td className="px-6 py-4 text-sm font-bold text-green-300">
                        {formatCurrency(
                          reportData.totals.total_billable_amount,
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-orange-300">
                        {formatCurrency(reportData.totals.total_cost_amount)}
                      </td>
                      <td className="px-6 py-4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* SR&ED Report Results */}
      {sredReportData && (
        <>
          {/* Overall Summary */}
          {sredReportData.overall_summary && (
            <div className={`${cardClass} rounded-xl p-6`}>
              <div className="flex justify-between items-center mb-4">
                <h2
                  className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t('sred.overallSummary')}
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      generateSredSimplePdf(sredReportData as any, t, {
                        start_date: filters.start_date,
                        end_date: filters.end_date,
                      })
                    }
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
                    title={
                      t('sred.exportSimplePdfHint') ||
                      'Basic format for sharing with accounting firm'
                    }
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                      />
                    </svg>
                    {t('sred.exportSimplePdf') || 'Export for Firm'}
                  </button>
                  <button
                    onClick={() =>
                      generateSredPdf(sredReportData as any, t, {
                        start_date: filters.start_date,
                        end_date: filters.end_date,
                      })
                    }
                    className={`inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-600 hover:bg-slate-700'}`}
                    title={
                      t('sred.exportDetailedPdfHint') ||
                      'Full detailed analysis report'
                    }
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    {t('sred.exportDetailedPdf') || 'Full Report'}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-4">
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}
                >
                  <div
                    className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    {sredReportData.overall_summary.total_projects_analyzed}
                  </div>
                  <div
                    className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                  >
                    {t('sred.projectsAnalyzed')}
                  </div>
                </div>
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}
                >
                  <div
                    className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    {Number(
                      sredReportData.overall_summary.total_hours || 0,
                    ).toFixed(1)}
                  </div>
                  <div
                    className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                  >
                    {t('sred.totalHours')}
                  </div>
                </div>
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}
                >
                  <div className="text-2xl font-bold text-green-700">
                    {Number(
                      sredReportData.overall_summary.estimated_eligible_hours ||
                        0,
                    ).toFixed(1)}
                  </div>
                  <div className="text-xs text-green-600">
                    {t('sred.eligibleHours')}
                  </div>
                </div>
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}
                >
                  <div className="text-2xl font-bold text-blue-700">
                    {Number(
                      sredReportData.overall_summary
                        .estimated_eligible_percentage || 0,
                    ).toFixed(1)}
                    %
                  </div>
                  <div className="text-xs text-blue-600">
                    {t('sred.eligiblePercentage')}
                  </div>
                </div>
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-purple-500/10' : 'bg-purple-50'}`}
                >
                  <div className="text-2xl font-bold text-purple-700">
                    {sredReportData.clients?.length || 0}
                  </div>
                  <div className="text-xs text-purple-600">
                    {t('sred.clients')}
                  </div>
                </div>
              </div>
              {/* Employee vs Contractor breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-indigo-500/10' : 'bg-indigo-50'}`}
                >
                  <div className="text-2xl font-bold text-indigo-700">
                    {Number(
                      sredReportData.overall_summary.employee_hours || 0,
                    ).toFixed(1)}
                  </div>
                  <div className="text-xs text-indigo-600">
                    {t('sred.employeeHours')}
                  </div>
                </div>
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-amber-500/10' : 'bg-amber-50'}`}
                >
                  <div className="text-2xl font-bold text-amber-700">
                    {Number(
                      sredReportData.overall_summary.contractor_hours || 0,
                    ).toFixed(1)}
                  </div>
                  <div className="text-xs text-amber-600">
                    {t('sred.contractorHours')}
                  </div>
                </div>
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-emerald-500/15' : 'bg-emerald-100'}`}
                >
                  <div className="text-2xl font-bold text-green-800">
                    {
                      sredReportData.overall_summary
                        .projects_with_high_eligibility
                    }
                  </div>
                  <div className="text-xs text-green-700">
                    {t('sred.highEligibility')}
                  </div>
                </div>
                <div
                  className={`text-center p-4 rounded-lg ${isDark ? 'bg-yellow-500/15' : 'bg-yellow-100'}`}
                >
                  <div className="text-2xl font-bold text-yellow-800">
                    {sredReportData.overall_summary
                      .projects_with_medium_eligibility +
                      sredReportData.overall_summary
                        .projects_with_low_eligibility}
                  </div>
                  <div className="text-xs text-yellow-700">
                    {t('sred.otherEligibility')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Client Details */}
          <div className="space-y-4">
            {sredReportData.clients?.map((client) => {
              const clientKey = client.client_id?.toString() || 'internal';
              return (
                <div
                  key={clientKey}
                  className={`${cardClass} rounded-xl overflow-hidden`}
                >
                  {/* Client Header */}
                  <button
                    onClick={() => toggleClient(clientKey)}
                    className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${isDark ? 'bg-slate-800/50 hover:bg-slate-800' : 'bg-gradient-to-r from-slate-100 to-slate-50 hover:from-slate-200 hover:to-slate-100'}`}
                  >
                    <div className="flex items-center space-x-4">
                      <svg
                        className={`w-5 h-5 text-slate-500 transform transition-transform ${expandedClients.has(clientKey) ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      <div className="text-left">
                        <h3
                          className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {client.client_name}
                        </h3>
                        <p
                          className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                        >
                          {client.projects.length} {t('sred.projects')} •{' '}
                          {Number(
                            client.client_summary?.total_hours || 0,
                          ).toFixed(1)}{' '}
                          {t('sred.hours')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {client.client_summary && (
                        <>
                          <div className="text-right">
                            <div className="text-sm font-medium text-indigo-600">
                              {Number(
                                client.client_summary.employee_hours || 0,
                              ).toFixed(1)}
                              h {t('sred.employees')}
                            </div>
                            <div className="text-sm font-medium text-amber-600">
                              {Number(
                                client.client_summary.contractor_hours || 0,
                              ).toFixed(1)}
                              h {t('sred.contractors')}
                            </div>
                          </div>
                          <div className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                            {Number(
                              client.client_summary
                                .estimated_eligible_percentage || 0,
                            ).toFixed(0)}
                            % {t('sred.eligible')}
                          </div>
                        </>
                      )}
                    </div>
                  </button>

                  {/* Client Projects */}
                  {expandedClients.has(clientKey) && (
                    <div
                      className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                    >
                      {client.projects.map((project) => (
                        <div
                          key={project.project_id}
                          className={`last:border-b-0 ${isDark ? 'border-b border-slate-700/50' : 'border-b border-slate-100'}`}
                        >
                          {/* Project Header */}
                          <button
                            onClick={() => toggleProject(project.project_id)}
                            className={`w-full px-6 py-3 flex items-center justify-between transition-colors ${isDark ? 'bg-slate-800/30 hover:bg-slate-800/60' : 'bg-slate-50 hover:bg-slate-100'}`}
                          >
                            <div className="flex items-center space-x-4 ml-8">
                              <svg
                                className={`w-4 h-4 transform transition-transform ${isDark ? 'text-slate-500' : 'text-slate-400'} ${expandedProjects.has(project.project_id) ? 'rotate-90' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                              <div className="text-left">
                                <h4
                                  className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                                >
                                  {project.project_name}
                                </h4>
                                <p
                                  className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                >
                                  {Number(project.total_hours || 0).toFixed(1)}{' '}
                                  {t('sred.hours')} • {project.total_entries}{' '}
                                  {t('sred.entries')} •
                                  <span className="text-indigo-600 ml-1">
                                    {Number(
                                      project.employee_hours || 0,
                                    ).toFixed(1)}
                                    h {t('sred.emp')}
                                  </span>{' '}
                                  /
                                  <span className="text-amber-600 ml-1">
                                    {Number(
                                      project.contractor_hours || 0,
                                    ).toFixed(1)}
                                    h {t('sred.ctr')}
                                  </span>
                                </p>
                              </div>
                            </div>
                            {project.ai_analysis?.sred_eligibility && (
                              <span
                                className={`px-3 py-1 text-sm font-medium rounded-full border ${getEligibilityColor(project.ai_analysis.sred_eligibility.eligibility_level)}`}
                              >
                                {project.ai_analysis.sred_eligibility.eligibility_level.toUpperCase()}{' '}
                                (
                                {
                                  project.ai_analysis.sred_eligibility
                                    .eligible_percentage
                                }
                                %)
                              </span>
                            )}
                          </button>

                          {/* Project Expanded Content */}
                          {expandedProjects.has(project.project_id) && (
                            <div
                              className={`p-6 ml-8 space-y-6 ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}
                            >
                              {project.error && (
                                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
                                  {t('sred.analysisError')}: {project.error}
                                </div>
                              )}

                              {/* Team breakdown */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {project.employees &&
                                  project.employees.length > 0 && (
                                    <div className="bg-indigo-50 rounded-lg p-3">
                                      <h5 className="text-sm font-medium text-indigo-800 mb-2">
                                        {t('sred.employees')} (
                                        {Number(
                                          project.employee_hours || 0,
                                        ).toFixed(1)}
                                        h)
                                      </h5>
                                      <div className="flex flex-wrap gap-1">
                                        {project.employees.map((emp, idx) => (
                                          <span
                                            key={idx}
                                            className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded"
                                          >
                                            {emp}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                {project.contractors &&
                                  project.contractors.length > 0 && (
                                    <div className="bg-amber-50 rounded-lg p-3">
                                      <h5 className="text-sm font-medium text-amber-800 mb-2">
                                        {t('sred.contractors')} (
                                        {Number(
                                          project.contractor_hours || 0,
                                        ).toFixed(1)}
                                        h)
                                      </h5>
                                      <div className="flex flex-wrap gap-1">
                                        {project.contractors.map((ctr, idx) => (
                                          <span
                                            key={idx}
                                            className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded"
                                          >
                                            {ctr}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                              </div>

                              {project.ai_analysis && (
                                <>
                                  {/* Executive Summary */}
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-blue-900 mb-2">
                                      {t('sred.executiveSummary')}
                                    </h4>
                                    <p className="text-sm text-blue-800 whitespace-pre-line">
                                      {project.ai_analysis.executive_summary}
                                    </p>
                                  </div>

                                  {/* Project Technical Summary */}
                                  {project.ai_analysis
                                    .project_technical_summary && (
                                    <div className="border-2 border-indigo-300 rounded-lg overflow-hidden">
                                      <div className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between">
                                        <h4 className="font-semibold flex items-center">
                                          <svg
                                            className="w-5 h-5 mr-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                          </svg>
                                          {t('sred.technicalSummary')}
                                        </h4>
                                        <div className="flex items-center space-x-2">
                                          <span className="text-xs text-indigo-200">
                                            {
                                              project.ai_analysis
                                                .project_technical_summary
                                                .word_count
                                            }{' '}
                                            {t('sred.words')}
                                          </span>
                                          <button
                                            onClick={() =>
                                              navigator.clipboard.writeText(
                                                project.ai_analysis
                                                  ?.project_technical_summary
                                                  ?.content || '',
                                              )
                                            }
                                            className="p-1 text-white hover:bg-indigo-500 rounded"
                                            title={t('common.copy')}
                                          >
                                            <svg
                                              className="w-4 h-4"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                      <div
                                        className={`p-4 ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}
                                      >
                                        <p
                                          className={`text-sm whitespace-pre-line leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                                        >
                                          {
                                            project.ai_analysis
                                              .project_technical_summary.content
                                          }
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Technology Stack */}
                                  {project.ai_analysis.technology_stack && (
                                    <div className="border-2 border-purple-300 rounded-lg overflow-hidden">
                                      <div className="bg-purple-600 text-white px-4 py-2 flex items-center justify-between">
                                        <h4 className="font-semibold flex items-center">
                                          <svg
                                            className="w-5 h-5 mr-2"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                                            />
                                          </svg>
                                          {t('sred.technologyStack')}
                                        </h4>
                                        <button
                                          onClick={() =>
                                            navigator.clipboard.writeText(
                                              project.ai_analysis
                                                ?.technology_stack?.content ||
                                                '',
                                            )
                                          }
                                          className="p-1 text-white hover:bg-purple-500 rounded"
                                          title={t('common.copy')}
                                        >
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                      <div
                                        className={`p-4 space-y-4 ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}
                                      >
                                        {/* Stack Summary Text */}
                                        <p
                                          className={`text-sm whitespace-pre-line leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                                        >
                                          {
                                            project.ai_analysis.technology_stack
                                              .content
                                          }
                                        </p>

                                        {/* Technical Details Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-purple-100">
                                          {/* Languages */}
                                          {(
                                            project.ai_analysis.technology_stack
                                              .languages ?? []
                                          ).length > 0 && (
                                            <div>
                                              <h5 className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">
                                                {t('sred.languages')}
                                              </h5>
                                              <div className="flex flex-wrap gap-1">
                                                {project.ai_analysis.technology_stack.languages.map(
                                                  (lang, i) => (
                                                    <span
                                                      key={i}
                                                      className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs"
                                                    >
                                                      {lang}
                                                    </span>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}

                                          {/* Frameworks */}
                                          {(
                                            project.ai_analysis.technology_stack
                                              .frameworks ?? []
                                          ).length > 0 && (
                                            <div>
                                              <h5 className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">
                                                {t('sred.frameworks')}
                                              </h5>
                                              <div className="flex flex-wrap gap-1">
                                                {project.ai_analysis.technology_stack.frameworks.map(
                                                  (fw, i) => (
                                                    <span
                                                      key={i}
                                                      className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                                                    >
                                                      {fw}
                                                    </span>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}

                                          {/* Libraries */}
                                          {(
                                            project.ai_analysis.technology_stack
                                              .libraries ?? []
                                          ).length > 0 && (
                                            <div>
                                              <h5 className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">
                                                {t('sred.libraries')}
                                              </h5>
                                              <div className="flex flex-wrap gap-1">
                                                {project.ai_analysis.technology_stack.libraries.map(
                                                  (lib, i) => (
                                                    <span
                                                      key={i}
                                                      className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-800'}`}
                                                    >
                                                      {lib}
                                                    </span>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}

                                          {/* Architectural Patterns */}
                                          {(
                                            project.ai_analysis.technology_stack
                                              .architectural_patterns ?? []
                                          ).length > 0 && (
                                            <div>
                                              <h5 className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">
                                                {t(
                                                  'sred.architecturalPatterns',
                                                )}
                                              </h5>
                                              <div className="flex flex-wrap gap-1">
                                                {project.ai_analysis.technology_stack.architectural_patterns.map(
                                                  (pattern, i) => (
                                                    <span
                                                      key={i}
                                                      className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs"
                                                    >
                                                      {pattern}
                                                    </span>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>

                                        {/* Custom Modules */}
                                        {(
                                          project.ai_analysis.technology_stack
                                            .custom_modules ?? []
                                        ).length > 0 && (
                                          <div className="pt-3 border-t border-purple-100">
                                            <h5 className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">
                                              {t('sred.customModules')}
                                            </h5>
                                            <div className="space-y-2">
                                              {project.ai_analysis.technology_stack.custom_modules.map(
                                                (mod, i) => (
                                                  <div
                                                    key={i}
                                                    className="bg-purple-50 rounded p-2"
                                                  >
                                                    <div className="font-medium text-sm text-purple-900">
                                                      {mod.name}
                                                    </div>
                                                    <div className="text-xs text-purple-700">
                                                      {mod.description}
                                                    </div>
                                                    {(mod.files ?? []).length >
                                                      0 && (
                                                      <div className="text-xs text-purple-500 mt-1">
                                                        {t('sred.files')}:{' '}
                                                        {mod.files.join(', ')}
                                                      </div>
                                                    )}
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Notable Customizations */}
                                        {(
                                          project.ai_analysis.technology_stack
                                            .notable_customizations ?? []
                                        ).length > 0 && (
                                          <div className="pt-3 border-t border-purple-100">
                                            <h5 className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">
                                              {t('sred.notableCustomizations')}
                                            </h5>
                                            <ul
                                              className={`list-disc list-inside text-sm space-y-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                                            >
                                              {project.ai_analysis.technology_stack.notable_customizations.map(
                                                (cust, i) => (
                                                  <li key={i}>{cust}</li>
                                                ),
                                              )}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Eligibility Details */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div
                                      className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                    >
                                      <h4
                                        className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                      >
                                        {t('sred.eligibilityDetails')}
                                      </h4>
                                      <div className="space-y-2">
                                        <div className="flex justify-between">
                                          <span
                                            className={
                                              isDark
                                                ? 'text-slate-400'
                                                : 'text-slate-600'
                                            }
                                          >
                                            {t('sred.confidence')}:
                                          </span>
                                          <span className="font-medium">
                                            {
                                              project.ai_analysis
                                                .sred_eligibility
                                                .confidence_percentage
                                            }
                                            %
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span
                                            className={
                                              isDark
                                                ? 'text-slate-400'
                                                : 'text-slate-600'
                                            }
                                          >
                                            {t('sred.eligibleHours')}:
                                          </span>
                                          <span className="font-medium">
                                            {Number(
                                              project.ai_analysis
                                                .sred_eligibility
                                                .eligible_hours_estimate || 0,
                                            ).toFixed(1)}
                                            h
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span
                                            className={
                                              isDark
                                                ? 'text-slate-400'
                                                : 'text-slate-600'
                                            }
                                          >
                                            {t('sred.eligiblePercentage')}:
                                          </span>
                                          <span className="font-medium">
                                            {
                                              project.ai_analysis
                                                .sred_eligibility
                                                .eligible_percentage
                                            }
                                            %
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {project.ai_analysis
                                      .documentation_quality && (
                                      <div
                                        className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                      >
                                        <h4
                                          className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                        >
                                          {t('sred.documentationQuality')}
                                        </h4>
                                        <div className="mb-2">
                                          <div className="flex justify-between text-sm mb-1">
                                            <span>{t('sred.score')}</span>
                                            <span>
                                              {
                                                project.ai_analysis
                                                  .documentation_quality.score
                                              }
                                              /100
                                            </span>
                                          </div>
                                          <div
                                            className={`w-full rounded-full h-2 ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
                                          >
                                            <div
                                              className={`h-2 rounded-full ${project.ai_analysis.documentation_quality.score >= 70 ? 'bg-green-500' : project.ai_analysis.documentation_quality.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                              style={{
                                                width: `${project.ai_analysis.documentation_quality.score}%`,
                                              }}
                                            ></div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Technological Uncertainties */}
                                  {(
                                    project.ai_analysis
                                      .technological_uncertainties ?? []
                                  ).length > 0 && (
                                    <div
                                      className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                    >
                                      <h4
                                        className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                      >
                                        {t('sred.technologicalUncertainties')}
                                      </h4>
                                      <div className="space-y-3">
                                        {(
                                          project.ai_analysis
                                            .technological_uncertainties ?? []
                                        ).map(
                                          (uncertainty: any, idx: number) => (
                                            <div
                                              key={idx}
                                              className={`rounded-md p-3 ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}
                                            >
                                              <p
                                                className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                                              >
                                                {uncertainty.description}
                                              </p>
                                              {uncertainty.technical_context && (
                                                <p
                                                  className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                                >
                                                  {
                                                    uncertainty.technical_context
                                                  }
                                                </p>
                                              )}
                                              <p
                                                className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                              >
                                                <span className="font-medium">
                                                  {t('sred.resolution')}:
                                                </span>{' '}
                                                {
                                                  uncertainty.resolution_approach
                                                }
                                              </p>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Hypotheses Tested */}
                                  {(project.ai_analysis.hypotheses_tested ?? [])
                                    .length > 0 && (
                                    <div
                                      className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                    >
                                      <h4
                                        className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                      >
                                        {t('sred.hypothesesTested')}
                                      </h4>
                                      <div className="space-y-3">
                                        {(
                                          project.ai_analysis
                                            .hypotheses_tested ?? []
                                        ).map(
                                          (hypothesis: any, idx: number) => (
                                            <div
                                              key={idx}
                                              className="bg-purple-50 rounded-md p-3"
                                            >
                                              <p className="font-medium text-purple-900">
                                                {hypothesis.hypothesis}
                                              </p>
                                              <p className="text-sm text-purple-700 mt-1">
                                                <span className="font-medium">
                                                  {t('sred.methodology')}:
                                                </span>{' '}
                                                {hypothesis.test_methodology}
                                              </p>
                                              <p className="text-sm text-purple-600 mt-1">
                                                <span className="font-medium">
                                                  {t('sred.outcome')}:
                                                </span>{' '}
                                                {hypothesis.outcome}
                                              </p>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Technical Results */}
                                  {(project.ai_analysis.technical_results ?? [])
                                    .length > 0 && (
                                    <div
                                      className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                    >
                                      <h4
                                        className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                      >
                                        {t('sred.technicalResults')}
                                      </h4>
                                      <div className="space-y-3">
                                        {(
                                          project.ai_analysis
                                            .technical_results ?? []
                                        ).map((result: any, idx: number) => (
                                          <div
                                            key={idx}
                                            className="bg-green-50 rounded-md p-3"
                                          >
                                            <p className="font-medium text-green-900">
                                              {result.description}
                                            </p>
                                            {result.metrics && (
                                              <p className="text-sm text-green-700 mt-1">
                                                <span className="font-medium">
                                                  {t('sred.metrics')}:
                                                </span>{' '}
                                                {result.metrics}
                                              </p>
                                            )}
                                            <p className="text-sm text-green-600 mt-1">
                                              {result.evidence}
                                            </p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Technological Advancements (legacy support) */}
                                  {(
                                    project.ai_analysis
                                      .technological_advancements ?? []
                                  ).length > 0 && (
                                    <div
                                      className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                    >
                                      <h4
                                        className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                      >
                                        {t('sred.technologicalAdvancements')}
                                      </h4>
                                      <div className="space-y-3">
                                        {(
                                          project.ai_analysis
                                            .technological_advancements ?? []
                                        ).map(
                                          (advancement: any, idx: number) => (
                                            <div
                                              key={idx}
                                              className="bg-green-50 rounded-md p-3"
                                            >
                                              <p className="font-medium text-green-900">
                                                {advancement.description}
                                              </p>
                                              <p className="text-sm text-green-700 mt-1">
                                                {advancement.evidence}
                                              </p>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Activity Classification */}
                                  {(
                                    project.ai_analysis
                                      .activity_classification ?? []
                                  ).length > 0 && (
                                    <div
                                      className={`border rounded-lg p-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                    >
                                      <h4
                                        className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                      >
                                        {t('sred.activityClassification')}
                                      </h4>
                                      <div className="overflow-x-auto">
                                        <table
                                          className={`min-w-full divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-200'}`}
                                        >
                                          <thead>
                                            <tr>
                                              <th
                                                className={`px-3 py-2 text-left text-xs font-medium uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                              >
                                                {t('sred.activity')}
                                              </th>
                                              <th
                                                className={`px-3 py-2 text-left text-xs font-medium uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                              >
                                                {t('sred.category')}
                                              </th>
                                              <th
                                                className={`px-3 py-2 text-center text-xs font-medium uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                              >
                                                {t('sred.complexity')}
                                              </th>
                                              <th
                                                className={`px-3 py-2 text-center text-xs font-medium uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                              >
                                                {t('sred.eligible')}
                                              </th>
                                              <th
                                                className={`px-3 py-2 text-left text-xs font-medium uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                              >
                                                {t('sred.justification')}
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody
                                            className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-200'}`}
                                          >
                                            {(
                                              project.ai_analysis
                                                .activity_classification ?? []
                                            ).map(
                                              (activity: any, idx: number) => (
                                                <tr key={idx}>
                                                  <td
                                                    className={`px-3 py-2 text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}
                                                  >
                                                    {activity.activity}
                                                  </td>
                                                  <td className="px-3 py-2">
                                                    <span
                                                      className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(activity.sred_category || activity.category)}`}
                                                    >
                                                      {(
                                                        activity.sred_category ||
                                                        activity.category ||
                                                        ''
                                                      ).replace('_', ' ')}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-center">
                                                    {activity.technical_complexity && (
                                                      <span
                                                        className={`px-2 py-1 text-xs font-medium rounded ${
                                                          activity.technical_complexity ===
                                                          'high'
                                                            ? 'bg-red-100 text-red-800'
                                                            : activity.technical_complexity ===
                                                                'medium'
                                                              ? 'bg-yellow-100 text-yellow-800'
                                                              : isDark
                                                                ? 'bg-slate-700 text-slate-300'
                                                                : 'bg-slate-100 text-slate-800'
                                                        }`}
                                                      >
                                                        {
                                                          activity.technical_complexity
                                                        }
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td className="px-3 py-2 text-center text-sm font-medium">
                                                    {
                                                      activity.eligible_percentage
                                                    }
                                                    %
                                                  </td>
                                                  <td
                                                    className={`px-3 py-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                                  >
                                                    {activity.justification}
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {/* Claim Recommendations */}
                                  {project.ai_analysis
                                    .claim_recommendations && (
                                    <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                                      <h4 className="font-semibold text-green-900 mb-3">
                                        {t('sred.claimRecommendations')}
                                      </h4>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                        <div>
                                          <span className="text-green-700">
                                            {t('sred.recommendedHours')}:
                                          </span>
                                          <span className="ml-2 font-bold text-green-900">
                                            {Number(
                                              project.ai_analysis
                                                .claim_recommendations
                                                .recommended_claim_hours || 0,
                                            ).toFixed(1)}
                                            h
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-green-700">
                                            {t('sred.recommendedPercentage')}:
                                          </span>
                                          <span className="ml-2 font-bold text-green-900">
                                            {
                                              project.ai_analysis
                                                .claim_recommendations
                                                .recommended_claim_percentage
                                            }
                                            %
                                          </span>
                                        </div>
                                        {project.ai_analysis
                                          .claim_recommendations
                                          .evidence_strength && (
                                          <div>
                                            <span className="text-green-700">
                                              {t('sred.evidenceStrength')}:
                                            </span>
                                            <span
                                              className={`ml-2 font-bold ${
                                                project.ai_analysis
                                                  .claim_recommendations
                                                  .evidence_strength ===
                                                'strong'
                                                  ? 'text-green-900'
                                                  : project.ai_analysis
                                                        .claim_recommendations
                                                        .evidence_strength ===
                                                      'moderate'
                                                    ? 'text-yellow-700'
                                                    : 'text-red-700'
                                              }`}
                                            >
                                              {
                                                project.ai_analysis
                                                  .claim_recommendations
                                                  .evidence_strength
                                              }
                                            </span>
                                          </div>
                                        )}
                                      </div>

                                      {((
                                        project.ai_analysis
                                          .claim_recommendations
                                          .key_points_for_claim ?? []
                                      ).length > 0 ||
                                        (
                                          project.ai_analysis
                                            .claim_recommendations
                                            .key_technical_points ?? []
                                        ).length > 0) && (
                                        <div className="mb-3">
                                          <h5 className="text-sm font-medium text-green-800 mb-1">
                                            {t('sred.keyPoints')}:
                                          </h5>
                                          <ul className="list-disc list-inside text-sm text-green-700">
                                            {(
                                              project.ai_analysis
                                                .claim_recommendations
                                                .key_points_for_claim ||
                                              project.ai_analysis
                                                .claim_recommendations
                                                .key_technical_points ||
                                              []
                                            ).map(
                                              (point: string, idx: number) => (
                                                <li key={idx}>{point}</li>
                                              ),
                                            )}
                                          </ul>
                                        </div>
                                      )}

                                      {((
                                        project.ai_analysis
                                          .claim_recommendations.risks ?? []
                                      ).length > 0 ||
                                        (
                                          project.ai_analysis
                                            .claim_recommendations
                                            .documentation_gaps ?? []
                                        ).length > 0) && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                                          <h5 className="text-sm font-medium text-yellow-800 mb-1">
                                            {project.ai_analysis
                                              .claim_recommendations
                                              .documentation_gaps
                                              ? t('sred.documentationGaps')
                                              : t('sred.risks')}
                                            :
                                          </h5>
                                          <ul className="list-disc list-inside text-sm text-yellow-700">
                                            {(
                                              project.ai_analysis
                                                .claim_recommendations.risks ||
                                              project.ai_analysis
                                                .claim_recommendations
                                                .documentation_gaps ||
                                              []
                                            ).map(
                                              (item: string, idx: number) => (
                                                <li key={idx}>{item}</li>
                                              ),
                                            )}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Xero Integration */}
      <XeroIntegration reportData={reportData} filters={filters} />
    </div>
  );
};

export default TimeEntryReports;
